import { useState, useEffect, useRef, useCallback } from "react";

// ── HOOK MOBILE ──────────────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

// ── DATOS INICIALES ──────────────────────────────────────────────
const VENDEDORES = [
  { id: "admin", nombre: "Admin", pin: "0000", rol: "admin" },
];

const REGIONES_INICIALES = ["Madrid", "Barcelona", "Valencia", "Sevilla", "Bilbao"];
const EMPRESAS_CAPTIO = ["Todas", "Demo"];

// Paleta por defecto (se sobreescribe con branding dinámico)
const C = {
  ink:       "var(--brand-ink)",
  inkMid:    "#444440",
  inkLite:   "#888884",
  paper:     "var(--brand-paper)",
  paperWarm: "#ece6db",
  rule:      "rgba(var(--brand-ink-rgb),0.12)",
  accent:    "var(--brand-ink)",
};

const COLUMNAS = [
  { id: "Nuevo",     label: "Nuevo",     color: "#888884", bg: "rgba(136,136,132,0.10)" },
  { id: "Validado",  label: "Validado",  color: "#444440", bg: "rgba(68,68,64,0.10)"    },
  { id: "Cotizado",  label: "Cotizado",  color: "#6B7280", bg: "rgba(107,114,128,0.10)" },
  { id: "Ganado",    label: "Ganado",    color: "var(--brand-ink)", bg: "rgba(var(--brand-ink-rgb),0.10)"    },
  { id: "Perdido",   label: "Perdido",   color: "#EF4444", bg: "rgba(239,68,68,0.10)"   },
];

const FUENTE_ICON = {
  "SerpAPI": "🔍", "LinkedIn": "💼", "Manual": "📁",
  "Referido": "🤝", "Web": "🌐", "Email": "📧",
};

// ── BRANDING DINÁMICO ────────────────────────────────────────────
// Slug del cliente derivado del subdominio (ej: "aarti" de aarti.velqo.io)
// En local (localhost) cae a "demo" para poder probar el template suelto.
const EMPRESA_SLUG = (() => {
  const host = window.location.hostname.split(".")[0];
  return host && host !== "localhost" ? host : "demo";
})();

const BRANDING_DEFAULT = {
  nombre:    "Tu Empresa",
  tagline:   "",
  logoUrl:   "",
  colorPrimario:   "#1a1917", // texto/acento principal
  colorSecundario: "#f4f1ec", // fondo
  firma:     "Equipo comercial",
  whatsapp:  "",
  web:       "",
  email:     "",
};

function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return "26,25,23";
  return [parseInt(m[1],16), parseInt(m[2],16), parseInt(m[3],16)].join(",");
}

let _brandingCache = null;
let _brandingPromise = null;
function fetchBranding() {
  if (_brandingCache) return Promise.resolve(_brandingCache);
  if (!_brandingPromise) {
    _brandingPromise = fetch(`/api/config/${EMPRESA_SLUG}`)
      .then(r => r.ok ? r.json() : {})
      .catch(() => ({}))
      .then(data => {
        _brandingCache = { ...BRANDING_DEFAULT, ...(data?.branding || {}) };
        return _brandingCache;
      });
  }
  return _brandingPromise;
}

// Hook que cualquier componente puede llamar — la primera vez dispara
// el fetch, las siguientes usan el cache en memoria (sin pedir de nuevo).
function useBranding() {
  const [branding, setBranding] = useState(_brandingCache || BRANDING_DEFAULT);
  useEffect(() => {
    fetchBranding().then(b => {
      setBranding(b);
      const root = document.documentElement.style;
      root.setProperty("--brand-ink", b.colorPrimario);
      root.setProperty("--brand-ink-rgb", hexToRgb(b.colorPrimario));
      root.setProperty("--brand-paper", b.colorSecundario);
      root.setProperty("--brand-paper-rgb", hexToRgb(b.colorSecundario));
    });
  }, []);
  return branding;
}

const LEADS_DEMO = [
  { id:1, empresa:"Cabify España SA", contacto:"Laura Gómez", cargo:"HR Manager", email:"lgomez@cabify.com", tel:"+34 91 123 4567", ciudad:"Madrid", region:"Madrid", segmento:"Tecnología", fuente:"SerpAPI", estado:"Nuevo", vendedor:"admin", score:92, monto:0, fecha:"2026-06-01", notas:"Equipo de 200 personas", empresa_captio:"Demo" },
  { id:2, empresa:"Bankinter SA", contacto:"Carlos Ruiz", cargo:"People & Culture", email:"cruiz@bankinter.com", tel:"+34 91 456 7890", ciudad:"Madrid", region:"Madrid", segmento:"Multinacionales", fuente:"LinkedIn", estado:"Validado", vendedor:"admin", score:88, monto:0, fecha:"2026-06-03", notas:"Interesados en taller de comunicación", empresa_captio:"Demo" },
  { id:3, empresa:"Deloitte España", contacto:"Ana Martín", cargo:"CHRO", email:"amartin@deloitte.es", tel:"+34 91 789 0123", ciudad:"Madrid", region:"Madrid", segmento:"Consultoras", fuente:"SerpAPI", estado:"Cotizado", vendedor:"admin", score:95, monto:2400, fecha:"2026-06-05", notas:"Presupuesto enviado para taller 5h", empresa_captio:"Demo" },
  { id:4, empresa:"Glovo Tech SL", contacto:"Marc Torres", cargo:"Head of People", email:"mtorres@glovoapp.com", tel:"+34 93 234 5678", ciudad:"Barcelona", region:"Barcelona", segmento:"Startups", fuente:"LinkedIn", estado:"Ganado", vendedor:"admin", score:90, monto:1800, fecha:"2026-05-28", notas:"Taller 2h confirmado para julio", empresa_captio:"Demo" },
  { id:5, empresa:"Garrigues Abogados", contacto:"Pilar Sánchez", cargo:"Socia Directora", email:"psanchez@garrigues.com", tel:"+34 91 567 8901", ciudad:"Madrid", region:"Madrid", segmento:"Gestorías", fuente:"Manual", estado:"Nuevo", vendedor:"admin", score:78, monto:0, fecha:"2026-06-08", notas:"Referido por cliente anterior", empresa_captio:"Demo" },
];

// ── HELPERS ──────────────────────────────────────────────────────
const fmt$ = v => v ? `€${Number(v).toLocaleString("es-ES")}` : "—";
const scoreColor = s => s >= 85 ? "#10B981" : s >= 68 ? "#F59E0B" : "#EF4444";

function useLocalStorage(key, initial) {
  const [val, setVal] = useState(() => {
    try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : initial; }
    catch { return initial; }
  });
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} }, [key, val]);
  return [val, setVal];
}

// Hook para cargar leads reales — siempre pisa los datos demo
function useLeadsFromFile(setLeads) {
  useEffect(() => {
    fetch("/api/leads")
      .then(r => {
        if (!r.ok) throw new Error("Sin servidor");
        return r.json();
      })
      .then(data => {
        if (!Array.isArray(data) || data.length === 0) return;
        const normalized = data.map((l, i) => ({
          id:       l.id || i + 1,
          empresa:  l.empresa || "",
          contacto: l.contacto || l.nombre_linkedin || l.nombre_contacto || "",
          cargo:    l.cargo_linkedin || l.cargo_decisor || l.cargo || "",
          email:    l.email_estimado || l.email || "",
          tel:      l.telefono || l.tel || "",
          ciudad:   l.ciudad || "",
          region:   l.region || "CABA",
          segmento: l.segmento || "",
          fuente:   l.fuente || "Agente IA",
          estado:   l.estado || "Nuevo",
          vendedor: l.vendedor || "",
          score:    Number(l.score) || 70,
          monto:    Number(l.monto) || 0,
          fecha:    l.fecha || new Date().toISOString().split("T")[0],
          notas:          l.notas || "",
          empresa_captio: l.empresa_captio || BRANDING_DEFAULT.nombre,
          nombre_linkedin: l.nombre_linkedin || "",
          url_linkedin:    l.url_linkedin || "",
          email_verificado: !!l.email_verificado,
          contactos_hunter: Array.isArray(l.contactos_hunter) ? l.contactos_hunter : [],
        }));
        // Limpiar localStorage y setear datos reales
        localStorage.removeItem("captio_leads");
        setLeads(normalized);
      })
      .catch(err => {
        console.warn("Server no disponible, usando datos locales:", err);
      });
  }, []);
}

// ── COMPONENTES BASE ─────────────────────────────────────────────
function ScoreBar({ score }) {
  const c = scoreColor(score);
  return (
    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
      <div style={{ flex:1, height:3, background:"rgba(255,255,255,0.08)", borderRadius:2, overflow:"hidden" }}>
        <div style={{ width:`${score}%`, height:"100%", background:c, borderRadius:2 }}/>
      </div>
      <span style={{ fontSize:10, color:c, fontWeight:700, minWidth:22 }}>{score}</span>
    </div>
  );
}

// ── MODAL MOVER LEAD ─────────────────────────────────────────────
function ModalMover({ lead, destino, onConfirm, onCancel }) {
  const [monto, setMonto] = useState(lead.monto || "");
  const [motivo, setMotivo] = useState("");

  const necesitaMonto  = destino === "Cotizado" || destino === "Ganado";
  const necesitaMotivo = destino === "Perdido";

  return (
    <div style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,0.7)",
      display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:16,
    }}>
      <div style={{
        background:"var(--brand-paper)", border:"1px solid rgba(var(--brand-ink-rgb),0.12)",
        borderRadius:16, padding:28, width:"100%", maxWidth:400,
      }}>
        <div style={{ fontSize:16, fontWeight:700, color:"var(--brand-ink)", marginBottom:6 }}>
          Mover a <span style={{ color: COLUMNAS.find(c=>c.id===destino)?.color }}>{destino}</span>
        </div>
        <div style={{ fontSize:13, color:"rgba(var(--brand-ink-rgb),0.5)", marginBottom:20 }}>
          {lead.empresa}
        </div>

        {necesitaMonto && (
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.4)", textTransform:"uppercase", letterSpacing:1 }}>
              {destino === "Cotizado" ? "Monto del presupuesto (ARS)" : "Monto del cierre (ARS)"}
            </label>
            <input
              type="number"
              value={monto}
              onChange={e => setMonto(e.target.value)}
              placeholder="ej: 150000"
              style={{
                width:"100%", marginTop:6, background:"rgba(255,255,255,0.06)",
                border:"1px solid rgba(255,255,255,0.12)", borderRadius:8,
                padding:"10px 14px", color:"var(--brand-ink)", fontSize:14, outline:"none", boxSizing:"border-box",
              }}
            />
          </div>
        )}

        {necesitaMotivo && (
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.4)", textTransform:"uppercase", letterSpacing:1 }}>
              Motivo de pérdida
            </label>
            <select
              value={motivo}
              onChange={e => setMotivo(e.target.value)}
              style={{
                width:"100%", marginTop:6, background:"#ffffff",
                border:"1px solid rgba(var(--brand-ink-rgb),0.15)", borderRadius:8,
                padding:"10px 14px", color:"var(--brand-ink)", fontSize:13, outline:"none", boxSizing:"border-box",
              }}
            >
              <option value="">Seleccionar motivo...</option>
              <option value="Precio">Precio — eligió otro proveedor más barato</option>
              <option value="Sin respuesta">Sin respuesta — no contestó</option>
              <option value="No calificó">No calificó — no era el perfil</option>
              <option value="Competencia">Competencia — eligió competidor</option>
              <option value="Postergado">Postergado — no es el momento</option>
              <option value="Otro">Otro motivo</option>
            </select>
          </div>
        )}

        <div style={{ display:"flex", gap:10, marginTop:8 }}>
          <button onClick={onCancel} style={{
            flex:1, padding:"10px 0", borderRadius:8, border:"1px solid rgba(255,255,255,0.1)",
            background:"transparent", color:"rgba(var(--brand-ink-rgb),0.5)", cursor:"pointer", fontSize:13,
          }}>Cancelar</button>
          <button
            onClick={() => onConfirm({ monto: Number(monto) || 0, motivo })}
            disabled={necesitaMotivo && !motivo}
            style={{
              flex:2, padding:"10px 0", borderRadius:8, border:"none",
              background: COLUMNAS.find(c=>c.id===destino)?.color || "var(--brand-ink)",
              color:"var(--brand-paper)", cursor:"pointer", fontSize:13, fontWeight:700,
              opacity: (necesitaMotivo && !motivo) ? 0.4 : 1,
            }}
          >Confirmar</button>
        </div>
      </div>
    </div>
  );
}

// ── MODAL DETALLE LEAD ───────────────────────────────────────────
function ModalDetalle({ lead, vendedores, onClose, onAsignar, onMoverACotizado, esAdmin, usuarioActual }) {
  const [enviandoEmail, setEnviandoEmail]           = useState(null); // tipo de template en envío, o null
  const [emailLog, setEmailLog]                     = useState([]);
  const [loadingLog, setLoadingLog]                 = useState(false);
  const [templatesDisponibles, setTemplatesDisponibles] = useState([]);
  const [tipoSeleccionado, setTipoSeleccionado]     = useState("");
  const [mostrarPresupuesto, setMostrarPresupuesto] = useState(false);
  const [descPresupuesto, setDescPresupuesto]       = useState("");
  const [montoNeto, setMontoNeto]                   = useState("");
  const [presupuestoEnviado, setPresupuestoEnviado] = useState(false);
  const branding = useBranding();

  const empKey = EMPRESA_SLUG;

  // Templates que el cliente armó (los 7 del flujo + los personalizados que haya creado)
  useEffect(() => {
    fetch(`/api/templates/${EMPRESA_SLUG}`)
      .then(r => r.json())
      .then(data => {
        const lista = Array.isArray(data) && data.length > 0 ? data : getTemplatesDefault(branding);
        setTemplatesDisponibles(lista);
        if (lista[0]) setTipoSeleccionado(lista[0].tipo);
      })
      .catch(() => {});
  }, []);

  const vendedor = vendedores.find(v => v.id === lead.vendedor);

  // Cargar historial de emails de este lead al abrir el modal
  useEffect(() => {
    setLoadingLog(true);
    fetch(`/api/actividad?lead_id=${lead.id}&limit=20`)
      .then(r => r.json())
      .then(data => { setEmailLog(Array.isArray(data) ? data : []); setLoadingLog(false); })
      .catch(() => setLoadingLog(false));
  }, [lead.id]);

  const enviarEmail = (tipo) => {
    const label = TIPOS_EMAIL.find(t => t.id === tipo)?.label || tipo;
    if (!window.confirm(`¿Confirmar envío de "${label}" a ${lead.empresa}?`)) return;
    setEnviandoEmail(tipo);
    fetch(`/api/email/${tipo}/${empKey}/${lead.id}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enviado_por: usuarioActual?.id || "sistema" }),
    })
    .then(r => r.json())
    .then(d => {
      setEnviandoEmail(null);
      if (d.ok) {
        alert(`✅ Email enviado correctamente`);
        // Refrescar log
        fetch(`/api/actividad?lead_id=${lead.id}&limit=20`)
          .then(r => r.json())
          .then(data => setEmailLog(Array.isArray(data) ? data : []))
          .catch(() => {});
      } else {
        alert(`❌ Error al enviar: ${d.error}`);
      }
    })
    .catch(() => { setEnviandoEmail(null); alert("❌ Error de conexión"); });
  };

  // ── Presupuesto: usa el template "8. Presupuesto" editable desde Emails ──
  const montoNum = (() => { const n = parseFloat(String(montoNeto).replace(/[^0-9.]/g,"")); return isNaN(n) ? 0 : n; })();
  const iva   = montoNum > 0 ? Math.round(montoNum * 0.21 * 100) / 100 : 0;
  const total = montoNum > 0 ? Math.round((montoNum + iva) * 100) / 100 : 0;

  const tplPresupuesto = templatesDisponibles.find(t => t.tipo === "presupuesto");

  const textoPresupuesto = () => {
    const base = tplPresupuesto ? tplPresupuesto.html : "Presupuesto para {{empresa}}: {{descripcion}} — Total: {{total}}";
    const plano = base
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();
    return plano
      .replace(/\{\{contacto\}\}/g, lead.contacto || lead.empresa || "")
      .replace(/\{\{empresa\}\}/g, lead.empresa || "")
      .replace(/\{\{descripcion\}\}/g, descPresupuesto || "")
      .replace(/\{\{monto\}\}/g, `€${montoNum.toLocaleString("es-ES")}`)
      .replace(/\{\{iva\}\}/g, `€${iva.toLocaleString("es-ES")}`)
      .replace(/\{\{total\}\}/g, `€${total.toLocaleString("es-ES")}`);
  };

  const formatWATel = (tel) => {
    if (!tel) return "";
    let n = tel.replace(/[^0-9]/g, "");
    if (n.startsWith("0")) n = n.slice(1);
    return n;
  };
  const waTel = formatWATel(lead.tel);
  const presupuestoMailto = `mailto:${lead.email}?subject=${encodeURIComponent(`Presupuesto — ${branding.nombre}`)}&body=${encodeURIComponent(textoPresupuesto())}`;
  const presupuestoWA = `https://wa.me/${waTel}?text=${encodeURIComponent(textoPresupuesto())}`;

  const marcarPresupuestoEnviado = () => {
    onMoverACotizado(lead.id, montoNum);
    setPresupuestoEnviado(true);
  };

  return (
    <div className="modal-overlay" style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,0.7)",
      display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:16,
    }}>
      <div className="modal-inner" style={{
        background:"var(--brand-paper)", border:"1px solid rgba(var(--brand-ink-rgb),0.1)",
        borderRadius:16, padding:28, width:"100%", maxWidth:480, maxHeight:"90vh", overflowY:"auto",
      }}>
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:20 }}>
          <div>
            <div style={{ fontSize:17, fontWeight:800, color:"var(--brand-ink)" }}>{lead.empresa}</div>
            <div style={{ fontSize:12, color:"rgba(var(--brand-ink-rgb),0.4)", marginTop:2 }}>{lead.segmento} · {lead.region}</div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"rgba(var(--brand-ink-rgb),0.4)", cursor:"pointer", fontSize:20 }}>✕</button>
        </div>

        {[
          ["Contacto", lead.contacto],
          ["Cargo", lead.cargo],
          ["Email", lead.email],
          ["Teléfono", lead.tel],
          ["Ciudad", lead.ciudad],
          ["Fuente", lead.fuente],
          ["Fecha", lead.fecha],
          ["Notas", lead.notas],
          lead.monto ? ["Monto", fmt$(lead.monto)] : null,
        ].filter(Boolean).map(([k,v]) => v ? (
          <div key={k} style={{ display:"flex", gap:12, marginBottom:10 }}>
            <span style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.35)", minWidth:70, textTransform:"uppercase", letterSpacing:.5, paddingTop:1 }}>{k}</span>
            <span style={{ fontSize:13, color:"rgba(var(--brand-ink-rgb),0.8)", flex:1 }}>{v}</span>
          </div>
        ) : null)}

        <div style={{ marginTop:8, marginBottom:16 }}>
          <span style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.35)", textTransform:"uppercase", letterSpacing:.5 }}>Score</span>
          <div style={{ marginTop:6 }}><ScoreBar score={lead.score}/></div>
        </div>

        {/* ── EMAILS ── */}
        <div style={{ marginTop:20, borderTop:"1px solid rgba(var(--brand-ink-rgb),0.08)", paddingTop:16 }}>
          <div style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.35)", textTransform:"uppercase", letterSpacing:.5, marginBottom:10 }}>
            Envío de emails
          </div>
          <div style={{ display:"flex", gap:8, marginBottom:14 }}>
            <select
              value={tipoSeleccionado}
              onChange={e => setTipoSeleccionado(e.target.value)}
              style={{
                flex:1, padding:"9px 10px", borderRadius:8, border:"1px solid rgba(var(--brand-ink-rgb),0.15)",
                background:"#fff", color:"var(--brand-ink)", fontSize:12, outline:"none",
              }}
            >
              {templatesDisponibles.map(t => (
                <option key={t.id} value={t.tipo}>{t.nombre}</option>
              ))}
            </select>
            <button
              onClick={() => enviarEmail(tipoSeleccionado)}
              disabled={!!enviandoEmail || !tipoSeleccionado}
              style={{
                padding:"9px 16px", borderRadius:8, border:"none",
                background: enviandoEmail ? "rgba(var(--brand-ink-rgb),0.05)" : "var(--brand-ink)",
                color: enviandoEmail ? "#888884" : "var(--brand-paper)",
                fontWeight:700, fontSize:12, cursor: enviandoEmail ? "not-allowed" : "pointer",
                whiteSpace:"nowrap",
              }}
            >
              {enviandoEmail ? "⏳ Enviando..." : "✉ Enviar"}
            </button>
          </div>

          {/* Historial de emails de este lead */}
          {loadingLog && <div style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.3)" }}>Cargando historial...</div>}
          {!loadingLog && emailLog.length > 0 && (
            <div>
              <div style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.25)", marginBottom:6 }}>Historial de contacto</div>
              {emailLog.map(log => {
                const tipoLabel = TIPOS_EMAIL.find(t => t.id === log.tipo)?.label || log.tipo;
                const fecha = new Date(log.creado_en).toLocaleString("es-AR", { day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit" });
                return (
                  <div key={log.id} style={{
                    display:"flex", justifyContent:"space-between", alignItems:"center",
                    padding:"5px 8px", borderRadius:6, marginBottom:4,
                    background: log.estado === "enviado" ? "rgba(16,185,129,0.07)" : "rgba(239,68,68,0.07)",
                    border:`1px solid ${log.estado === "enviado" ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.15)"}`,
                  }}>
                    <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                      <span style={{ fontSize:10, color: log.estado === "enviado" ? "#10B981" : "#EF4444" }}>
                        {log.estado === "enviado" ? "✓" : "✗"}
                      </span>
                      <span style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.65)" }}>{tipoLabel}</span>
                      {log.enviado_por && log.enviado_por !== "sistema" && (
                        <span style={{ fontSize:9, color:"rgba(var(--brand-ink-rgb),0.3)" }}>· {log.enviado_por}</span>
                      )}
                    </div>
                    <span style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.3)" }}>{fecha}</span>
                  </div>
                );
              })}
            </div>
          )}
          {!loadingLog && emailLog.length === 0 && (
            <div style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.2)", textAlign:"center", padding:"6px 0" }}>
              Sin emails enviados aún
            </div>
          )}
        </div>

        {/* ── PRESUPUESTO ── */}
        <div style={{ marginTop:20, borderTop:"1px solid rgba(var(--brand-ink-rgb),0.08)", paddingTop:16 }}>
          <button onClick={() => { setMostrarPresupuesto(!mostrarPresupuesto); setPresupuestoEnviado(false); }} style={{
            width:"100%", padding:"10px", borderRadius:8, border:"none",
            background: mostrarPresupuesto ? "rgba(var(--brand-ink-rgb),0.12)" : "rgba(var(--brand-ink-rgb),0.06)",
            color:"var(--brand-ink)", fontWeight:700, fontSize:13, cursor:"pointer",
          }}>
            📄 {mostrarPresupuesto ? "Cerrar presupuesto" : "Generar presupuesto"}
          </button>

          {mostrarPresupuesto && !presupuestoEnviado && (
            <div style={{ marginTop:14 }}>
              {!tplPresupuesto && (
                <div style={{ fontSize:11, color:"#B45309", background:"rgba(245,158,11,0.1)", border:"1px solid rgba(245,158,11,0.25)", borderRadius:8, padding:"8px 10px", marginBottom:12 }}>
                  ⚠️ No hay un template de tipo "Presupuesto" guardado todavía — se va a usar un texto genérico. Armalo desde la pestaña de Emails para personalizarlo.
                </div>
              )}
              <div style={{ marginBottom:12 }}>
                <label style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.35)", textTransform:"uppercase", letterSpacing:.5, display:"block", marginBottom:6 }}>
                  Descripción del servicio
                </label>
                <textarea
                  value={descPresupuesto}
                  onChange={e => setDescPresupuesto(e.target.value)}
                  placeholder="Ej: paquete de 10 horas de consultoría mensual"
                  rows={4}
                  style={{
                    width:"100%", background:"#ffffff", border:"1px solid rgba(var(--brand-ink-rgb),0.15)",
                    borderRadius:8, padding:"10px 12px", color:"var(--brand-ink)", fontSize:12,
                    outline:"none", resize:"vertical", boxSizing:"border-box",
                  }}
                />
              </div>

              <div style={{ marginBottom:12 }}>
                <label style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.35)", textTransform:"uppercase", letterSpacing:.5, display:"block", marginBottom:6 }}>
                  Monto neto (sin IVA)
                </label>
                <input
                  type="text"
                  value={montoNeto}
                  onChange={e => setMontoNeto(e.target.value)}
                  placeholder="Ej: 1500"
                  style={{
                    width:"100%", background:"#ffffff", border:"1px solid rgba(var(--brand-ink-rgb),0.15)",
                    borderRadius:8, padding:"10px 12px", color:"var(--brand-ink)", fontSize:13,
                    outline:"none", boxSizing:"border-box",
                  }}
                />
              </div>

              {montoNum > 0 && (
                <div style={{ background:"rgba(var(--brand-ink-rgb),0.04)", border:"1px solid rgba(var(--brand-ink-rgb),0.1)", borderRadius:8, padding:"10px 14px", marginBottom:14 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"rgba(var(--brand-ink-rgb),0.5)", marginBottom:4 }}>
                    <span>Subtotal</span><span>€{montoNum.toLocaleString("es-ES")}</span>
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"rgba(var(--brand-ink-rgb),0.5)", marginBottom:6 }}>
                    <span>IVA 21%</span><span>€{iva.toLocaleString("es-ES")}</span>
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:14, fontWeight:800, color:"var(--brand-ink)", borderTop:"1px solid rgba(var(--brand-ink-rgb),0.1)", paddingTop:6 }}>
                    <span>TOTAL</span><span>€{total.toLocaleString("es-ES")}</span>
                  </div>
                </div>
              )}

              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                <a href={presupuestoMailto} onClick={marcarPresupuestoEnviado} style={{
                  display:"block", textAlign:"center", padding:"10px", borderRadius:8,
                  background:"rgba(59,130,246,0.15)", color:"#3B82F6", textDecoration:"none",
                  fontSize:13, fontWeight:600,
                }}>📧 Enviar por email</a>

                <a href={presupuestoWA} target="_blank" rel="noreferrer" onClick={marcarPresupuestoEnviado} style={{
                  display:"block", textAlign:"center", padding:"10px", borderRadius:8,
                  background:"rgba(37,211,102,0.15)", color:"#25D366", textDecoration:"none",
                  fontSize:13, fontWeight:600,
                }}>💬 Enviar por WhatsApp</a>
              </div>
            </div>
          )}

          {presupuestoEnviado && (
            <div style={{ marginTop:14, background:"rgba(16,185,129,0.1)", border:"1px solid rgba(16,185,129,0.3)", borderRadius:8, padding:"12px 14px", textAlign:"center" }}>
              <div style={{ fontSize:13, fontWeight:700, color:"#10B981", marginBottom:2 }}>✅ Lead movido a Cotizado</div>
              <div style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.4)" }}>Total €{total.toLocaleString("es-ES")}</div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

// ── CARD LEAD ────────────────────────────────────────────────────
function LeadCard({ lead, columnaColor, onDragStart, onTap, esAdmin, vendedores }) {
  const branding = useBranding();
  const vendedor = vendedores.find(v => v.id === lead.vendedor);
  return (
    <div
      draggable
      onDragStart={e => onDragStart(e, lead.id)}
      onClick={() => onTap(lead)}
      style={{
        background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.07)",
        borderLeft:`3px solid ${columnaColor}`, borderRadius:10, padding:"12px 14px",
        cursor:"grab", marginBottom:8, transition:"transform .15s, box-shadow .15s",
        userSelect:"none",
      }}
      onMouseEnter={e => { e.currentTarget.style.transform="translateY(-1px)"; e.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,0.3)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform=""; e.currentTarget.style.boxShadow=""; }}
    >
      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
        {(() => {
          const empColors = {
            [branding.nombre]: { bg:"rgba(var(--brand-ink-rgb),0.12)", color:"var(--brand-ink)" },
          };
          const ec = empColors[lead.empresa_captio] || { bg:"rgba(139,92,246,0.15)", color:"#8B5CF6" };
          return (
            <span style={{
              fontSize:9, fontWeight:700, padding:"2px 8px", borderRadius:10,
              background:ec.bg, color:ec.color, whiteSpace:"nowrap",
            }}>{lead.empresa_captio || branding.nombre}</span>
          );
        })()}
      </div>
      <div style={{ fontSize:13, fontWeight:700, color:"var(--brand-ink)", marginBottom:2, lineHeight:1.3 }}>{lead.empresa}</div>
      <div style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.45)", marginBottom:8 }}>{lead.contacto} · {lead.cargo}</div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
        <span style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.3)" }}>{FUENTE_ICON[lead.fuente] || "📌"} {lead.fuente}</span>
        <span style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.3)" }}>{lead.region}</span>
      </div>
      <ScoreBar score={lead.score}/>
      {lead.monto > 0 && (
        <div style={{ marginTop:6, fontSize:12, fontWeight:700, color:"#F59E0B" }}>{fmt$(lead.monto)}</div>
      )}
      {esAdmin && vendedor && (
        <div style={{ marginTop:6, fontSize:10, color:"rgba(var(--brand-ink-rgb),0.3)" }}>👤 {vendedor.nombre}</div>
      )}
    </div>
  );
}

// ── COLUMNA KANBAN ───────────────────────────────────────────────
function Columna({ col, leads, onDrop, onDragOver, onDragStart, onTap, esAdmin, vendedores }) {
  const total$ = leads.filter(l=>l.monto>0).reduce((a,b)=>a+b.monto,0);
  return (
    <div
      style={{ flex:1, minWidth:0 }}
      onDragOver={onDragOver}
      onDrop={e => onDrop(e, col.id)}
    >
      <div style={{
        background: col.bg, border:`1px solid ${col.color}33`,
        borderRadius:12, padding:"10px 12px", marginBottom:8,
        display:"flex", justifyContent:"space-between", alignItems:"center",
      }}>
        <div>
          <span style={{ fontSize:12, fontWeight:700, color:col.color }}>{col.label}</span>
          <span style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.35)", marginLeft:8 }}>{leads.length}</span>
        </div>
        {total$ > 0 && <span style={{ fontSize:11, fontWeight:700, color:col.color }}>{fmt$(total$)}</span>}
      </div>
      <div style={{ minHeight:120 }}>
        {leads.map(lead => (
          <LeadCard
            key={lead.id}
            lead={lead}
            columnaColor={col.color}
            onDragStart={onDragStart}
            onTap={onTap}
            esAdmin={esAdmin}
            vendedores={vendedores}
          />
        ))}
      </div>
    </div>
  );
}

// ── LOGIN ────────────────────────────────────────────────────────
function Login({ vendedores, onLogin }) {
  const branding = useBranding();
  const multiUsuario = vendedores.length > 1;
  // Con un único usuario, ya queda "seleccionado" — pide el PIN igual, sin
  // pantalla de "¿Quién sos?" de por medio.
  const [seleccionado, setSeleccionado] = useState(multiUsuario ? null : vendedores[0]);
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");

  // vendedores llega hardcodeado al montar y se reemplaza cuando responde
  // el fetch a /api/vendedores — hay que resincronizar "seleccionado" (y su
  // PIN real) cuando eso pasa, en vez de quedarse con el valor inicial.
  useEffect(() => {
    if (!multiUsuario) setSeleccionado(vendedores[0]);
  }, [vendedores, multiUsuario]);

  const intentarEntrar = () => {
    if (pin === seleccionado.pin) {
      setError("");
      onLogin(seleccionado);
    } else {
      setError("PIN incorrecto");
      setPin("");
    }
  };

  return (
    <div style={{
      minHeight:"100vh", background:"var(--brand-paper)", display:"flex",
      alignItems:"center", justifyContent:"center", padding:24,
      fontFamily:"Georgia,'Times New Roman',serif",
    }}>
      <div style={{ textAlign:"center", maxWidth:360, width:"100%" }}>
        {/* Logo */}
        {branding.logoUrl ? (
          <img src={branding.logoUrl} alt={branding.nombre} style={{ maxHeight:56, marginBottom:8 }}/>
        ) : (
          <div style={{
            fontFamily:"Georgia,serif", fontSize:38, fontWeight:"normal",
            color:"var(--brand-ink)", letterSpacing:"0.08em", marginBottom:8,
          }}>{branding.nombre}</div>
        )}
        {branding.tagline && (
          <div style={{
            fontSize:11, letterSpacing:"0.22em", textTransform:"uppercase",
            color:"#888884", marginBottom:multiUsuario?32:48,
          }}>{branding.tagline}</div>
        )}

        {multiUsuario && !seleccionado && (
          <>
            <div style={{ fontSize:13, color:"#888884", fontFamily:"sans-serif", marginBottom:18 }}>
              ¿Quién sos?
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:8 }}>
              {vendedores.map(v => (
                <button key={v.id} onClick={() => { setSeleccionado(v); setError(""); }}
                  style={{
                    padding:"12px 24px", background:"transparent", color:"var(--brand-ink)",
                    border:"1px solid rgba(var(--brand-ink-rgb),0.2)", borderRadius:8,
                    fontFamily:"Georgia,serif", fontSize:14, fontStyle:"italic",
                    cursor:"pointer", letterSpacing:"0.02em",
                  }}>
                  {v.nombre}
                </button>
              ))}
            </div>
          </>
        )}

        {seleccionado && (
          <>
            <div style={{ fontSize:15, fontStyle:"italic", color:"#444440", marginBottom:16, fontFamily:"Georgia,serif" }}>
              Hola, {seleccionado.nombre}. Ingresá tu PIN.
            </div>
            <input
              type="password" inputMode="numeric" maxLength={4} value={pin}
              onChange={e => setPin(e.target.value.replace(/[^0-9]/g,""))}
              onKeyDown={e => e.key === "Enter" && pin.length === 4 && intentarEntrar()}
              autoFocus
              style={{
                width:140, textAlign:"center", fontSize:24, letterSpacing:"0.4em",
                padding:"10px 0", border:"1px solid rgba(var(--brand-ink-rgb),0.2)",
                borderRadius:8, marginBottom:10, fontFamily:"sans-serif",
                background:"rgba(var(--brand-ink-rgb),0.02)", color:"var(--brand-ink)",
              }}
            />
            {error && <div style={{ fontSize:12, color:"#DC2626", fontFamily:"sans-serif", marginBottom:10 }}>{error}</div>}
            <div style={{ display:"flex", gap:8, justifyContent:"center" }}>
              {multiUsuario && (
                <button onClick={() => { setSeleccionado(null); setPin(""); setError(""); }}
                  style={{ padding:"10px 18px", background:"transparent", color:"rgba(var(--brand-ink-rgb),0.5)",
                    border:"1px solid rgba(var(--brand-ink-rgb),0.15)", borderRadius:8,
                    fontFamily:"sans-serif", fontSize:12, cursor:"pointer" }}>
                  ← Volver
                </button>
              )}
              <button onClick={intentarEntrar} disabled={pin.length !== 4}
                style={{ padding:"10px 24px", background:"var(--brand-ink)", color:"var(--brand-paper)",
                  border:"none", borderRadius:8, fontFamily:"sans-serif", fontSize:12, fontWeight:600,
                  cursor: pin.length === 4 ? "pointer" : "not-allowed", opacity: pin.length === 4 ? 1 : 0.4 }}>
                Entrar
              </button>
            </div>
          </>
        )}

        <div style={{ marginTop:48, fontSize:10, letterSpacing:"0.18em", textTransform:"uppercase", color:"#888884" }}>
          by velqo.io
        </div>
      </div>
    </div>
  );
}

// ── PANEL ADMIN — Agente ─────────────────────────────────────────
function PanelAgente({ regiones, onAgregarRegion }) {
  const [cfg, setCfg] = useState({ segmento:"construcción", ciudad:"Córdoba", radio:"40" });
  const [corriendo, setCorriendo] = useState(false);
  const [logs, setLogs] = useState([]);
  const [nuevaRegion, setNuevaRegion] = useState("");
  const logRef = useRef(null);

  const pasos = [
    `🔎 Iniciando agente — segmento: "${cfg.segmento}"`,
    `📍 Radio: ${cfg.radio} km desde ${cfg.ciudad}`,
    `📋 Páginas Amarillas → buscando...`,
    `   ✅ 6 empresas encontradas`,
    `📋 Guía Oleo → buscando...`,
    `   ✅ 4 empresas encontradas`,
    `📋 Cylex → buscando...`,
    `   ✅ 3 empresas encontradas`,
    `🔍 SerpAPI → buscando...`,
    `   ✅ 5 empresas encontradas`,
    `🤖 Groq → calificando 18 empresas únicas...`,
    `   ⭐ "Constructora Roca SRL" → score 89`,
    `   ⭐ "Metalúrgica Norte SA" → score 84`,
    `   ⚠️  "Talleres Express" → score 52 (descartado)`,
    `   ⭐ "Ingeniería Sur SRL" → score 77`,
    `💾 4 leads nuevos guardados`,
    `✅ Agente finalizado`,
  ];

  const ejecutar = () => {
    setCorriendo(true); setLogs([]);
    let i = 0;
    const iv = setInterval(() => {
      if (i < pasos.length) {
        setLogs(p => [...p, { t: new Date().toLocaleTimeString("es-AR"), m: pasos[i] }]);
        i++;
        setTimeout(() => { if (logRef.current) logRef.current.scrollTop = 9999; }, 30);
      } else {
        clearInterval(iv);
        setCorriendo(false);
      }
    }, 650);
  };

  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
      {/* Config agente */}
      <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:20 }}>
        <div style={{ fontWeight:700, fontSize:14, color:"var(--brand-ink)", marginBottom:16 }}>⚙️ Configurar búsqueda</div>
        {[
          { label:"Segmento / Rubro", key:"segmento", placeholder:"ej: construcción" },
          { label:"Ciudad base",      key:"ciudad",   placeholder:"ej: Córdoba" },
          { label:"Radio (km)",       key:"radio",    placeholder:"ej: 40" },
        ].map(f => (
          <div key={f.key} style={{ marginBottom:12 }}>
            <div style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.35)", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{f.label}</div>
            <input
              value={cfg[f.key]}
              onChange={e => setCfg(p => ({...p,[f.key]:e.target.value}))}
              placeholder={f.placeholder}
              style={{
                width:"100%", background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.09)",
                borderRadius:8, padding:"9px 12px", color:"var(--brand-ink)", fontSize:13, outline:"none", boxSizing:"border-box",
              }}
            />
          </div>
        ))}
        <button
          onClick={ejecutar}
          disabled={corriendo}
          style={{
            width:"100%", padding:"11px 0", borderRadius:9, border:"none",
            background: corriendo ? "rgba(255,107,0,0.2)" : "linear-gradient(135deg,var(--brand-ink),#444440)",
            color:"var(--brand-ink)", fontWeight:700, fontSize:13, cursor: corriendo ? "not-allowed" : "pointer",
          }}
        >{corriendo ? "⏳ Ejecutando..." : "▶ Ejecutar Agente"}</button>

        {logs.length > 0 && (
          <div ref={logRef} style={{
            marginTop:14, background:"rgba(0,0,0,0.4)", borderRadius:8, padding:12,
            fontFamily:"monospace", fontSize:11, maxHeight:160, overflowY:"auto",
          }}>
            {logs.map((l,i) => (
              <div key={i} style={{ color:"rgba(var(--brand-ink-rgb),0.65)", marginBottom:3, display:"flex", gap:8 }}>
                <span style={{ color:"rgba(var(--brand-ink-rgb),0.2)", minWidth:64 }}>{l.t}</span>
                <span>{l.m}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Regiones */}
      <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:20 }}>
        <div style={{ fontWeight:700, fontSize:14, color:"var(--brand-ink)", marginBottom:16 }}>🗺️ Regiones activas</div>
        {regiones.map(r => (
          <div key={r} style={{
            display:"flex", alignItems:"center", gap:8, padding:"8px 12px",
            background:"rgba(255,107,0,0.08)", border:"1px solid rgba(255,107,0,0.2)",
            borderRadius:8, marginBottom:8,
          }}>
            <div style={{ width:6, height:6, borderRadius:"50%", background:"var(--brand-ink)" }}/>
            <span style={{ fontSize:13, color:"rgba(var(--brand-ink-rgb),0.8)" }}>{r}</span>
          </div>
        ))}
        <div style={{ display:"flex", gap:8, marginTop:12 }}>
          <input
            value={nuevaRegion}
            onChange={e => setNuevaRegion(e.target.value)}
            placeholder="Nueva región..."
            onKeyDown={e => {
              if (e.key==="Enter" && nuevaRegion.trim()) {
                onAgregarRegion(nuevaRegion.trim());
                setNuevaRegion("");
              }
            }}
            style={{
              flex:1, background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.09)",
              borderRadius:8, padding:"8px 12px", color:"var(--brand-ink)", fontSize:13, outline:"none",
            }}
          />
          <button
            onClick={() => { if(nuevaRegion.trim()) { onAgregarRegion(nuevaRegion.trim()); setNuevaRegion(""); }}}
            style={{
              padding:"8px 14px", borderRadius:8, border:"none",
              background:"rgba(59,130,246,0.2)", color:"var(--brand-ink)", cursor:"pointer", fontWeight:700,
            }}
          >+</button>
        </div>
      </div>
    </div>
  );
}

// ── KNOWLEDGE VIEWER ──────────────────────────────────────────────
function KnowledgeViewer({ empresa, refreshKey }) {
  const [data, setData] = useState({ texto:"", chars:0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`/api/knowledge/${empresa}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [empresa, refreshKey]);

  const limpiar = () => {
    if (!window.confirm("¿Borrar todo el conocimiento de esta empresa?")) return;
    fetch(`/api/knowledge/${empresa}/clear`, { method:"POST" })
      .then(() => setData({ texto:"", chars:0 }))
      .catch(() => {});
  };

  return (
    <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:20 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:13, color:"var(--brand-ink)" }}>Conocimiento actual</div>
          <div style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.3)", marginTop:2 }}>
            {data.chars > 0 ? `${data.chars} caracteres cargados` : "Sin conocimiento cargado aún"}
          </div>
        </div>
        {data.chars > 0 && (
          <button onClick={limpiar} style={{
            padding:"5px 12px", borderRadius:7, border:"1px solid rgba(239,68,68,0.3)",
            background:"rgba(239,68,68,0.08)", color:"#EF4444", fontSize:11, cursor:"pointer",
          }}>🗑 Limpiar</button>
        )}
      </div>
      {loading && <div style={{ fontSize:12, color:"rgba(var(--brand-ink-rgb),0.3)" }}>Cargando...</div>}
      {!loading && data.chars === 0 && (
        <div style={{ fontSize:12, color:"rgba(var(--brand-ink-rgb),0.2)", textAlign:"center", padding:"20px 0" }}>
          No hay conocimiento cargado. Subí un archivo arriba para empezar.
        </div>
      )}
      {!loading && data.chars > 0 && (
        <textarea
          readOnly
          value={data.texto}
          style={{
            width:"100%", height:200, background:"rgba(0,0,0,0.3)",
            border:"1px solid rgba(255,255,255,0.08)", borderRadius:8,
            padding:"10px 12px", color:"rgba(var(--brand-ink-rgb),0.6)", fontSize:11,
            resize:"vertical", outline:"none", fontFamily:"monospace",
          }}
        />
      )}
    </div>
  );
}

// ── TAB ACTIVIDAD ────────────────────────────────────────────────
function TabActividad({ leads, esAdmin }) {
  const [logs, setLogs]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [filtroEmp, setFiltroEmp] = useState("todas");
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const branding = useBranding();

  const cargar = () => {
    setLoading(true);
    let url = "/api/actividad?limit=100";
    if (filtroEmp !== "todas") url += `&empresa=${filtroEmp}`;
    fetch(url)
      .then(r => r.json())
      .then(data => { setLogs(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { cargar(); }, [filtroEmp]);

  const logsFiltrados = filtroTipo === "todos" ? logs : logs.filter(l => l.tipo === filtroTipo);

  const tipoLabel  = { presentacion:"Presentación", "5preguntas":"5 Preguntas", presupuesto:"Presupuesto", minerva_ok:"Minerva ✓", minerva_vendedor:"Minerva → Vendedor" };
  const tipoColor  = { presentacion:"#3B82F6", "5preguntas":"#8B5CF6", presupuesto:"#F59E0B", minerva_ok:"#10B981", minerva_vendedor:"var(--brand-ink)" };
  const empColor   = { aarti:"var(--brand-ink)" };
  const empLabel   = { [EMPRESA_SLUG]: branding.nombre };

  return (
    <div>
      {/* Filtros */}
      <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
        <div style={{ fontSize:15, fontWeight:800, color:"var(--brand-ink)", flex:1 }}>⚡ Actividad del sistema</div>
        <select value={filtroEmp} onChange={e => setFiltroEmp(e.target.value)} style={{
          background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)",
          borderRadius:8, padding:"7px 12px", color:"var(--brand-ink)", fontSize:12, outline:"none",
        }}>
          <option value="todas">Todas las empresas</option>
          <option value={EMPRESA_SLUG}>{branding.nombre}</option>
          
          
        </select>
        <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)} style={{
          background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)",
          borderRadius:8, padding:"7px 12px", color:"var(--brand-ink)", fontSize:12, outline:"none",
        }}>
          <option value="todos">Todos los tipos</option>
          <option value="presentacion">Presentación</option>
          <option value="5preguntas">5 Preguntas</option>
          <option value="presupuesto">Presupuesto</option>
          <option value="minerva_ok">Minerva ✓</option>
          <option value="minerva_vendedor">Minerva → Vendedor</option>
        </select>
        <button onClick={cargar} style={{
          padding:"7px 14px", borderRadius:8, border:"1px solid rgba(255,255,255,0.1)",
          background:"transparent", color:"rgba(var(--brand-ink-rgb),0.5)", cursor:"pointer", fontSize:12,
        }}>↻ Actualizar</button>
      </div>

      {/* Tabla de actividad */}
      {loading && <div style={{ fontSize:13, color:"rgba(var(--brand-ink-rgb),0.3)", textAlign:"center", padding:40 }}>Cargando...</div>}
      {!loading && logsFiltrados.length === 0 && (
        <div style={{ fontSize:13, color:"rgba(var(--brand-ink-rgb),0.2)", textAlign:"center", padding:60 }}>
          Sin actividad registrada aún
        </div>
      )}
      {!loading && logsFiltrados.length > 0 && (
        <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, overflow:"hidden" }}>
          {logsFiltrados.map((log, i) => {
            const lead = leads.find(l => String(l.id) === String(log.lead_id));
            const fecha = new Date(log.creado_en).toLocaleString("es-AR", {
              day:"2-digit", month:"2-digit", year:"2-digit", hour:"2-digit", minute:"2-digit"
            });
            return (
              <div key={log.id} style={{
                display:"flex", alignItems:"center", gap:12, padding:"11px 16px",
                borderBottom: i < logsFiltrados.length-1 ? "1px solid rgba(255,255,255,0.05)" : "none",
              }}>
                {/* Estado */}
                <span style={{ fontSize:14, minWidth:16 }}>{log.estado === "enviado" ? "✅" : "❌"}</span>

                {/* Tipo */}
                <span style={{
                  fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:10, whiteSpace:"nowrap",
                  background:`${tipoColor[log.tipo] || "#6B7280"}20`,
                  color: tipoColor[log.tipo] || "#6B7280",
                }}>
                  {tipoLabel[log.tipo] || log.tipo}
                </span>

                {/* Empresa captio */}
                <span style={{
                  fontSize:9, fontWeight:700, padding:"2px 7px", borderRadius:10, whiteSpace:"nowrap",
                  background:`${empColor[log.empresa] || "#6B7280"}20`,
                  color: empColor[log.empresa] || "#6B7280",
                }}>
                  {empLabel[log.empresa] || log.empresa}
                </span>

                {/* Lead */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12, color:"var(--brand-ink)", fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {lead?.empresa || `Lead #${log.lead_id}`}
                  </div>
                  <div style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.35)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {log.destinatario}
                  </div>
                </div>

                {/* Enviado por */}
                {log.enviado_por && (
                  <span style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.3)", whiteSpace:"nowrap" }}>
                    👤 {log.enviado_por}
                  </span>
                )}

                {/* Fecha */}
                <span style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.25)", whiteSpace:"nowrap" }}>{fecha}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* KPIs rápidos */}
      {!loading && logs.length > 0 && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))", gap:10, marginTop:16 }}>
          {[
            { label:"Total enviados",     value: logs.filter(l=>l.estado==="enviado").length, color:"#10B981" },
            { label:"Presentaciones",     value: logs.filter(l=>l.tipo==="presentacion").length, color:"#3B82F6" },
            { label:"5 Preguntas",        value: logs.filter(l=>l.tipo==="5preguntas").length, color:"#8B5CF6" },
            { label:"Errores",            value: logs.filter(l=>l.estado==="error").length, color:"#EF4444" },
          ].map(k => (
            <div key={k.label} style={{
              background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)",
              borderRadius:10, padding:"12px 14px",
            }}>
              <div style={{ fontSize:22, fontWeight:900, color:k.color }}>{k.value}</div>
              <div style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.4)", marginTop:4 }}>{k.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── TEMPLATES DEFAULT ────────────────────────────────────────────
// Templates por defecto — neutros, parametrizados por branding (b).
// OJO: estos HTML se mandan tal cual a la bandeja del lead — usar SIEMPRE
// hex reales acá (b.colorPrimario / b.colorSecundario), nunca var(--...),
// porque los clientes de email no cargan el CSS de la página.
function getTemplatesDefault(b) {
  const ink = b.colorPrimario, paper = b.colorSecundario;
  const wa = b.whatsapp ? `https://wa.me/${b.whatsapp.replace(/[^0-9]/g,"")}` : "#";
  const footer = [b.web, b.email].filter(Boolean).join(" · ") || "&nbsp;";

  // Wrapper común: header con marca + footer — evita repetir el armado HTML 7 veces
  const wrap = (cuerpo, ctaTexto) => `<table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;font-family:Georgia,'Times New Roman',serif;background:${paper};border:1px solid #e0dbd4;">
  <tr><td style="padding:40px 40px 20px 40px;text-align:center;border-bottom:1px solid #e0dbd4;">
    <div style="font-size:28px;font-weight:normal;color:${ink};letter-spacing:0.08em;">${b.nombre}</div>
    ${b.tagline ? `<div style="font-size:10px;letter-spacing:0.22em;text-transform:uppercase;color:#888884;margin-top:4px;">${b.tagline}</div>` : ""}
  </td></tr>
  <tr><td style="padding:36px 40px 20px 40px;">
    ${cuerpo}
    ${ctaTexto ? `<table cellpadding="0" cellspacing="0" style="margin:0 0 28px 0;">
      <tr><td style="background:${ink};padding:14px 32px;">
        <a href="${wa}" style="color:${paper};text-decoration:none;font-family:Georgia,serif;font-size:13px;font-style:italic;letter-spacing:0.04em;">${ctaTexto}</a>
      </td></tr>
    </table>` : ""}
    <p style="font-size:13px;color:#444440;margin:16px 0 0 0;font-style:italic;">${b.firma}<br>${b.nombre}</p>
  </td></tr>
  <tr><td style="padding:16px 40px;border-top:1px solid #e0dbd4;text-align:center;">
    <p style="font-size:10px;color:#aaa9a6;margin:0;letter-spacing:0.1em;">${footer}</p>
  </td></tr>
</table>`;

  const p = (txt) => `<p style="font-size:14px;color:#444440;line-height:1.8;margin:0 0 16px 0;">${txt}</p>`;
  const saludo = (txt) => `<p style="font-size:15px;color:${ink};line-height:1.7;margin:0 0 16px 0;">${txt}</p>`;

  return [
    {
      id: "presentacion",
      nombre: "1. Presentación (día 0)",
      tipo: "presentacion",
      html: wrap(
        saludo(`Estimado/a <strong>{{contacto}}</strong>,`) +
        p(`Me pongo en contacto desde <strong>${b.nombre}</strong> porque creo que podemos aportarle valor a {{empresa}}.`) +
        p(`Me encantaría contarle más sobre cómo trabajamos y ver si encaja con lo que están buscando.`) +
        p(`Contame si preferís que te enviemos una propuesta con precios, o que te contactemos directamente para charlarlo.`),
        "Conversemos →"
      ),
    },
    {
      id: "reintento1",
      nombre: "2. Reintento — día 30",
      tipo: "reintento1",
      html: wrap(
        saludo(`Hola <strong>{{contacto}}</strong>,`) +
        p(`Hace unas semanas le escribí para presentarle <strong>${b.nombre}</strong>. No quería que se pierda en la bandeja de entrada — sigo a disposición si le interesa charlarlo.`) +
        p(`¿Le viene bien que le enviemos una propuesta, o prefiere que lo llamemos?`),
        "Conversemos →"
      ),
    },
    {
      id: "reintento2",
      nombre: "3. Reintento — día 60",
      tipo: "reintento2",
      html: wrap(
        saludo(`Hola <strong>{{contacto}}</strong>,`) +
        p(`Quería retomar el contacto. Trabajamos con varias empresas de su sector resolviendo necesidades como las de {{empresa}}, y creo que podríamos sumarle valor.`) +
        p(`Si es buen momento, me cuenta y avanzamos.`),
        "Conversemos →"
      ),
    },
    {
      id: "reintento3",
      nombre: "4. Reintento — día 90 (último)",
      tipo: "reintento3",
      html: wrap(
        saludo(`Hola <strong>{{contacto}}</strong>,`) +
        p(`Quería volver a escribirle por si todavía les puede servir lo que hacemos en <strong>${b.nombre}</strong>. Si en este momento no es prioridad, no hay problema — quedamos a disposición para cuando lo sea.`),
        "Conversemos →"
      ),
    },
    {
      id: "gracias_presupuesto",
      nombre: "5. Gracias — pidió presupuesto",
      tipo: "gracias_presupuesto",
      html: wrap(
        saludo(`¡Gracias por su respuesta, <strong>{{contacto}}</strong>!`) +
        p(`Tomamos nota de su interés en recibir una propuesta. En las próximas 24-48hs alguien de nuestro equipo le va a enviar el presupuesto con el detalle de precios.`) +
        p(`Cualquier consulta mientras tanto, escríbanos.`),
        null
      ),
    },
    {
      id: "gracias_contacto",
      nombre: "6. Gracias — pidió que lo contacten",
      tipo: "gracias_contacto",
      html: wrap(
        saludo(`¡Gracias por su respuesta, <strong>{{contacto}}</strong>!`) +
        p(`Tomamos nota — en las próximas 24-48hs alguien de nuestro equipo se va a comunicar con usted para charlar los detalles.`) +
        p(`Que tenga buen día.`),
        null
      ),
    },
    {
      id: "gracias_otro",
      nombre: "7. Gracias — respuesta general",
      tipo: "gracias_otro",
      html: wrap(
        saludo(`¡Gracias por su respuesta, <strong>{{contacto}}</strong>!`) +
        p(`Recibimos su mensaje. En las próximas 24-48hs alguien de nuestro equipo le va a responder personalmente.`) +
        p(`Saludos.`),
        null
      ),
    },
    {
      id: "presupuesto",
      nombre: "8. Presupuesto",
      tipo: "presupuesto",
      html: wrap(
        saludo(`Estimado/a <strong>{{contacto}}</strong>,`) +
        p(`Le acercamos el presupuesto solicitado para {{empresa}}.`) +
        p(`<strong>Detalle:</strong> {{descripcion}}`) +
        p(`<strong>Monto neto:</strong> {{monto}}<br><strong>IVA:</strong> {{iva}}<br><strong>Total:</strong> {{total}}`) +
        p(`Quedamos a disposición para cualquier consulta.`),
        null
      ),
    },
  ];
}

const TIPOS_EMAIL = [
  { id: "presentacion",         label: "1. Presentación"        },
  { id: "reintento1",           label: "2. Reintento día 30"    },
  { id: "reintento2",           label: "3. Reintento día 60"    },
  { id: "reintento3",           label: "4. Reintento día 90"    },
  { id: "gracias_presupuesto",  label: "5. Gracias (presupuesto)" },
  { id: "gracias_contacto",     label: "6. Gracias (contacto)"  },
  { id: "gracias_otro",         label: "7. Gracias (otro)"      },
  { id: "presupuesto",          label: "8. Presupuesto"         },
  { id: "custom",               label: "Personalizado"          },
];


// ── TAB EMAIL EDITOR ─────────────────────────────────────────────
// ── TAB SEGUIMIENTO DE EMAILS (aperturas, rebotes, timeline por lead) ──
function TabSeguimiento({ leads }) {
  const [logs, setLogs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filtro, setFiltro]       = useState("todos");
  const [busqueda, setBusqueda]   = useState("");
  const [hiloAbierto, setHiloAbierto] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch(`/api/actividad?empresa=${EMPRESA_SLUG}&limit=300`)
      .then(r => r.json())
      .then(data => { setLogs(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Agrupar logs por lead_id
  const porLead = {};
  logs.forEach(log => {
    const key = log.lead_id;
    if (!porLead[key]) porLead[key] = { lead_id: key, email: log.destinatario, envios: [], estados: new Set() };
    porLead[key].envios.push(log);
    porLead[key].estados.add(log.estado);
    if (log.abierto_en) porLead[key].abierto = true;
    if (log.veces_abierto > 0) porLead[key].vecesAbierto = (porLead[key].vecesAbierto || 0) + log.veces_abierto;
  });

  const leadsAgrupados = Object.values(porLead).map(g => {
    const lead = leads.find(l => String(l.id) === String(g.lead_id));
    const ultimo = g.envios[0];
    let estadoFinal = "enviado";
    if (g.estados.has("rebote")) estadoFinal = "rebote";
    else if (g.estados.has("baja")) estadoFinal = "baja";
    else if (lead?.etiqueta_respuesta) estadoFinal = "respondido";
    else if (lead?.estado === "Contestado" || lead?.estado === "Validado") estadoFinal = "respondido";
    else if (g.abierto) estadoFinal = "abierto";
    return { ...g, lead, ultimo, estadoFinal };
  });

  // Métricas
  const total = leadsAgrupados.length;
  const abiertos = leadsAgrupados.filter(l => l.abierto).length;
  const respondidos = leadsAgrupados.filter(l => l.estadoFinal === "respondido").length;
  const bajas = leadsAgrupados.filter(l => l.estadoFinal === "baja").length;
  const rebotes = leadsAgrupados.filter(l => l.estadoFinal === "rebote").length;
  const pctAbiertos = total ? Math.round(abiertos / total * 100) : 0;
  const pctRespondidos = total ? Math.round(respondidos / total * 100) : 0;

  // Filtrar
  let filtrados = leadsAgrupados;
  if (filtro === "abiertos") filtrados = filtrados.filter(l => l.abierto && l.estadoFinal !== "respondido");
  else if (filtro === "sin_abrir") filtrados = filtrados.filter(l => !l.abierto && l.estadoFinal === "enviado");
  else if (filtro === "respondidos") filtrados = filtrados.filter(l => l.estadoFinal === "respondido");
  else if (filtro === "bajas") filtrados = filtrados.filter(l => l.estadoFinal === "baja");
  else if (filtro === "rebotes") filtrados = filtrados.filter(l => l.estadoFinal === "rebote");

  if (busqueda) {
    const q = busqueda.toLowerCase();
    filtrados = filtrados.filter(l =>
      (l.lead?.empresa || "").toLowerCase().includes(q) ||
      (l.email || "").toLowerCase().includes(q)
    );
  }

  const estadoBadge = (est) => {
    const map = {
      enviado:    { bg: "rgba(59,130,246,0.15)", color: "#2563EB", label: "enviado" },
      abierto:    { bg: "rgba(16,185,129,0.15)", color: "#059669", label: "abierto" },
      respondido: { bg: "rgba(99,102,241,0.15)", color: "#4F46E5", label: "respondido" },
      baja:       { bg: "rgba(239,68,68,0.15)",  color: "#DC2626", label: "baja" },
      rebote:     { bg: "rgba(239,68,68,0.15)",  color: "#DC2626", label: "rebote" },
    };
    const s = map[est] || map.enviado;
    return { padding:"3px 10px", borderRadius:20, fontSize:10, fontWeight:700, background:s.bg, color:s.color, display:"inline-block" };
  };

  const formatFecha = (f) => {
    if (!f) return { fecha: "", rel: "" };
    const d = new Date(f);
    const ahora = new Date();
    const diff = Math.floor((ahora - d) / 1000);
    const rel = diff < 3600 ? `hace ${Math.floor(diff/60)}m` : diff < 86400 ? `hace ${Math.floor(diff/3600)}h` : `hace ${Math.floor(diff/86400)}d`;
    return { fecha: d.toLocaleString("es-AR", { day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit" }), rel };
  };

  if (loading) return <div style={{ textAlign:"center", padding:60, color:"rgba(var(--brand-ink-rgb),0.3)", fontSize:13 }}>Cargando seguimiento...</div>;

  return (
    <div>
      {/* Métricas */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(110px,1fr))", gap:10, marginBottom:16 }}>
        {[
          { label:"Enviados", value:total, color:"var(--brand-ink)" },
          { label:"Abiertos", value:abiertos, pct:pctAbiertos, color:"#059669" },
          { label:"Respondidos", value:respondidos, pct:pctRespondidos, color:"#4F46E5" },
          { label:"Bajas", value:bajas, color:"#D97706" },
          { label:"Rebotes", value:rebotes, color:"#DC2626" },
        ].map(k => (
          <div key={k.label} style={{ background:"rgba(var(--brand-ink-rgb),0.03)", border:"1px solid rgba(var(--brand-ink-rgb),0.08)", borderRadius:10, padding:"12px 14px" }}>
            <div style={{ fontSize:20, fontWeight:900, color:k.color }}>{k.value}{k.pct !== undefined ? <span style={{ fontSize:12, fontWeight:600, opacity:0.6 }}> ({k.pct}%)</span> : null}</div>
            <div style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.4)", marginTop:4 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
        <select value={filtro} onChange={e => setFiltro(e.target.value)} style={{
          background:"var(--brand-paper)", border:"1px solid rgba(var(--brand-ink-rgb),0.15)",
          borderRadius:8, padding:"7px 12px", color:"var(--brand-ink)", fontSize:12, outline:"none",
        }}>
          <option value="todos">Todos</option>
          <option value="abiertos">Abiertos (sin responder)</option>
          <option value="sin_abrir">Sin abrir</option>
          <option value="respondidos">Respondidos</option>
          <option value="bajas">Bajas</option>
          <option value="rebotes">Rebotes</option>
        </select>
        <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar por empresa o email..." style={{
          flex:1, minWidth:180, background:"var(--brand-paper)", border:"1px solid rgba(var(--brand-ink-rgb),0.15)",
          borderRadius:8, padding:"7px 12px", color:"var(--brand-ink)", fontSize:12, outline:"none",
        }}/>
      </div>

      {filtrados.length === 0 && (
        <div style={{ fontSize:13, color:"rgba(var(--brand-ink-rgb),0.3)", textAlign:"center", padding:60 }}>
          Sin envíos registrados aún
        </div>
      )}

      {filtrados.length > 0 && (
        <div style={{ background:"rgba(var(--brand-ink-rgb),0.02)", border:"1px solid rgba(var(--brand-ink-rgb),0.07)", borderRadius:14, overflow:"hidden" }}>
          {filtrados.map((l, i) => {
            const ult = formatFecha(l.ultimo?.creado_en);
            const abierto = l.ultimo?.abierto_en ? formatFecha(l.ultimo.abierto_en) : null;
            const abierta = hiloAbierto === l.lead_id;
            return (
              <div key={l.lead_id} style={{ borderBottom: i < filtrados.length-1 ? "1px solid rgba(var(--brand-ink-rgb),0.05)" : "none" }}>
                <div onClick={() => setHiloAbierto(abierta ? null : l.lead_id)} style={{
                  display:"flex", alignItems:"center", gap:12, padding:"11px 16px", cursor:"pointer",
                }}>
                  <span style={{ fontSize:14, minWidth:16 }}>{l.ultimo?.estado === "enviado" ? "✅" : l.ultimo?.estado === "rebote" ? "📛" : "❌"}</span>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:12, color:"var(--brand-ink)", fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {l.lead?.empresa || `Lead #${l.lead_id}`}
                    </div>
                    <div style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.4)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {l.email}
                    </div>
                  </div>
                  <span style={estadoBadge(l.estadoFinal)}>{l.estadoFinal}</span>
                  {abierto && <div style={{ fontSize:11, color:"#059669", display:"flex", alignItems:"center", gap:6, whiteSpace:"nowrap" }}>👁 {abierto.rel}{l.vecesAbierto > 1 && <span style={{ fontSize:9, fontWeight:800, padding:"1px 6px", borderRadius:10, background:"rgba(5,150,105,0.15)", color:"#059669" }}>x{l.vecesAbierto}</span>}</div>}
                  <span style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.3)", whiteSpace:"nowrap" }}>{ult.fecha}</span>
                  <span style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.25)" }}>{abierta ? "▲" : "▼"}</span>
                </div>
                {abierta && (
                  <div style={{ padding:"4px 16px 14px 44px", display:"flex", flexDirection:"column", gap:8 }}>
                    {[...l.envios].reverse().map((e, j) => {
                      const f = formatFecha(e.creado_en);
                      const a = e.abierto_en ? formatFecha(e.abierto_en) : null;
                      return (
                        <div key={j} style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.5)", display:"flex", gap:8, alignItems:"center" }}>
                          <span style={estadoBadge(e.estado)}>{e.tipo || e.estado}</span>
                          <span>{f.fecha}</span>
                          {a && <span style={{ color:"#059669" }}>👁 abierto {a.rel}</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── TAB SALUD DE EMAIL (termómetro SPF/DKIM/DMARC/MX + métricas) ──
function TabEmailHealth() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchHealth = () => {
    setLoading(true);
    fetch(`/api/email-health/${EMPRESA_SLUG}`)
      .then(r => r.ok ? r.json() : null)
      .then(h => { setHealth(h); setLoading(false); })
      .catch(() => { setHealth(null); setLoading(false); });
  };

  useEffect(() => { fetchHealth(); }, []);

  const getScoreColor = (score) => {
    if (score >= 80) return "#059669";
    if (score >= 60) return "#D97706";
    if (score >= 40) return "#EA580C";
    return "#DC2626";
  };

  const getEnfriamientoLabel = (e) => {
    if (e === "critico") return { label:"BLOQUEADO", color:"#DC2626", bg:"rgba(220,38,38,0.1)" };
    if (e === "frio")    return { label:"ENFRIADO", color:"#EA580C", bg:"rgba(234,88,12,0.1)" };
    if (e === "tibio")   return { label:"REDUCIDO", color:"#D97706", bg:"rgba(217,119,6,0.1)" };
    return { label:"NORMAL", color:"#059669", bg:"rgba(5,150,105,0.1)" };
  };

  if (loading) return <div style={{ textAlign:"center", padding:60, color:"rgba(var(--brand-ink-rgb),0.3)", fontSize:13 }}>Verificando salud de emails...</div>;

  if (!health) return (
    <div style={{ textAlign:"center", padding:60, color:"rgba(var(--brand-ink-rgb),0.3)" }}>
      <div style={{ fontSize:40, marginBottom:12 }}>📧</div>
      <div style={{ fontSize:14 }}>No hay SMTP configurado</div>
      <div style={{ fontSize:12, marginTop:4 }}>Configurá el correo en Admin → SMTP para ver la salud</div>
    </div>
  );

  const h = health;
  const sc = h.score;
  const scColor = getScoreColor(sc);
  const enf = getEnfriamientoLabel(h.enfriamiento);

  return (
    <div style={{ background:"rgba(var(--brand-ink-rgb),0.02)", border:"1px solid rgba(var(--brand-ink-rgb),0.08)", borderRadius:12, overflow:"hidden" }}>
      <div style={{ padding:"16px 20px", borderBottom:"1px solid rgba(var(--brand-ink-rgb),0.06)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontWeight:700, fontSize:14, color:"var(--brand-ink)" }}>{h.checks?.email}</span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, background:enf.bg, color:enf.color }}>
            {enf.label} — máx {h.maxDiario}/día
          </span>
          <button onClick={fetchHealth} style={{
            padding:"5px 12px", borderRadius:7, border:"1px solid rgba(var(--brand-ink-rgb),0.15)",
            background:"transparent", color:"rgba(var(--brand-ink-rgb),0.5)", cursor:"pointer", fontSize:11,
          }}>↻ Actualizar</button>
        </div>
      </div>

      <div style={{ padding:20 }}>
        <div style={{ display:"grid", gridTemplateColumns:"200px 1fr", gap:24 }}>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center" }}>
            <svg width="160" height="160" viewBox="0 0 160 160">
              <circle cx="80" cy="80" r="68" fill="none" stroke="rgba(var(--brand-ink-rgb),0.08)" strokeWidth="12"/>
              <circle cx="80" cy="80" r="68" fill="none" stroke={scColor} strokeWidth="12"
                strokeDasharray={`${(sc / 100) * 427} 427`}
                strokeLinecap="round"
                transform="rotate(-90 80 80)"
                style={{ transition:"stroke-dasharray 0.8s ease" }}
              />
              <text x="80" y="72" textAnchor="middle" fill={scColor} fontSize="36" fontWeight="800">{sc}</text>
              <text x="80" y="92" textAnchor="middle" fill="rgba(var(--brand-ink-rgb),0.4)" fontSize="12">/ 100</text>
            </svg>
            <div style={{ marginTop:8, fontSize:12, color:scColor, fontWeight:700 }}>
              {sc >= 80 ? "Excelente" : sc >= 60 ? "Aceptable" : sc >= 40 ? "Mejorable" : "Crítico"}
            </div>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            {[
              { label:"SPF", data:h.checks?.spf, desc:"Autoriza servidores de envío" },
              { label:"DKIM", data:h.checks?.dkim, desc:"Firma digital del email" },
              { label:"DMARC", data:h.checks?.dmarc, desc:"Política de autenticación" },
              { label:"MX", data:h.checks?.mx, desc:"Registros de correo" },
            ].map(c => (
              <div key={c.label} style={{ padding:"12px 14px", background:"rgba(var(--brand-ink-rgb),0.02)", border:"1px solid rgba(var(--brand-ink-rgb),0.06)", borderRadius:8 }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
                  <span style={{ fontWeight:700, fontSize:12, color:"var(--brand-ink)" }}>{c.label}</span>
                  <span style={{
                    fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:12,
                    background: c.data?.ok ? "rgba(5,150,105,0.1)" : "rgba(220,38,38,0.1)",
                    color: c.data?.ok ? "#059669" : "#DC2626",
                  }}>{c.data?.ok ? "OK" : "FALTA"}</span>
                </div>
                <div style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.4)" }}>{c.desc}</div>
                {c.data?.warning && <div style={{ fontSize:10, color:"#D97706", marginTop:4 }}>⚠ {c.data.warning}</div>}
              </div>
            ))}
          </div>
        </div>

        {h.checks?.ip && (
          <div style={{ marginTop:16, padding:"12px 14px", background:"rgba(var(--brand-ink-rgb),0.02)", border:"1px solid rgba(var(--brand-ink-rgb),0.06)", borderRadius:8 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
              <span style={{ fontWeight:700, fontSize:12, color:"var(--brand-ink)" }}>Listas negras — IP {h.checks.ip}</span>
              <span style={{
                fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:12,
                background: h.checks.blacklists?.length === 0 ? "rgba(5,150,105,0.1)" : "rgba(220,38,38,0.1)",
                color: h.checks.blacklists?.length === 0 ? "#059669" : "#DC2626",
              }}>
                {h.checks.blacklists?.length === 0 ? "LIMPIO" : `${h.checks.blacklists?.length} LISTA(S)`}
              </span>
            </div>
            {h.checks.blacklists?.length > 0 && (
              <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                {h.checks.blacklists.map(bl => (
                  <span key={bl.list} style={{ fontSize:10, padding:"2px 8px", borderRadius:12, background:"rgba(220,38,38,0.08)", color:"#DC2626" }}>
                    {bl.list}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {h.metricas?.totalHistorico > 0 && (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginTop:16 }}>
            {[
              { label:"Enviados", value:h.metricas.totalHistorico, color:"#2563EB" },
              { label:"Abiertos", value:h.metricas.abiertosHistorico, color:"#059669" },
              { label:"Errores", value:h.metricas.erroresHistorico, color:"#DC2626" },
              { label:"Tasa apertura", value: h.metricas.tasaApertura !== null ? h.metricas.tasaApertura + "%" : "—", color:"#D97706" },
            ].map(m => (
              <div key={m.label} style={{ textAlign:"center", padding:"10px", background:"rgba(var(--brand-ink-rgb),0.02)", border:"1px solid rgba(var(--brand-ink-rgb),0.06)", borderRadius:8 }}>
                <div style={{ fontSize:22, fontWeight:800, color:m.color }}>{m.value}</div>
                <div style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.35)", marginTop:2 }}>{m.label}</div>
              </div>
            ))}
          </div>
        )}

        {h.recomendaciones?.length > 0 && (
          <div style={{ marginTop:16 }}>
            <div style={{ fontWeight:700, fontSize:12, marginBottom:8, color:"rgba(var(--brand-ink-rgb),0.6)" }}>Recomendaciones</div>
            {h.recomendaciones.map((rec, i) => (
              <div key={i} style={{ display:"flex", gap:8, alignItems:"flex-start", padding:"8px 0", borderBottom: i < h.recomendaciones.length - 1 ? "1px solid rgba(var(--brand-ink-rgb),0.04)" : "none" }}>
                <span style={{ color:"#D97706", fontSize:12, flexShrink:0 }}>⚠</span>
                <span style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.5)", lineHeight:"1.4" }}>{rec}</span>
              </div>
            ))}
          </div>
        )}

        {h.checkedAt && (
          <div style={{ marginTop:12, fontSize:10, color:"rgba(var(--brand-ink-rgb),0.2)", textAlign:"right" }}>
            Verificado: {new Date(h.checkedAt).toLocaleString("es-AR")}
          </div>
        )}
      </div>
    </div>
  );
}

// ── TAB EMAILS (envoltorio con sub-tabs: Seguimiento / Plantillas / Salud) ──
function TabEmails({ esAdmin, schedule, setSchedule, leads, pin }) {
  const [subTab, setSubTab] = useState("seguimiento");

  const subtabs = [
    { id: "seguimiento", label: "📊 Seguimiento" },
    { id: "plantillas",  label: "📝 Plantillas" },
    { id: "salud",       label: "🌡️ Salud Email" },
  ];

  return (
    <div>
      {esAdmin && (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16, padding:"12px 16px", background:"rgba(var(--brand-ink-rgb),0.02)", border:"1px solid rgba(var(--brand-ink-rgb),0.08)", borderRadius:10 }}>
          <div>
            <div style={{ fontWeight:700, fontSize:13, color:"var(--brand-ink)" }}>Envío automático de emails</div>
            <div style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.4)", marginTop:2 }}>
              {schedule.envio_automatico
                ? "El sistema envía presentaciones y seguimientos automáticamente"
                : "Los emails solo se envían de forma manual desde la ficha del lead"}
            </div>
          </div>
          <button onClick={() => {
            const nuevo = !schedule.envio_automatico;
            if (!window.confirm(nuevo
              ? "¿Activar el envío automático de emails? El sistema enviará presentaciones y seguimientos según la secuencia configurada."
              : "¿Desactivar el envío automático? Los emails solo se podrán enviar manualmente."
            )) return;
            const nuevoSchedule = { ...schedule, envio_automatico: nuevo };
            setSchedule(nuevoSchedule);
            fetch(`/api/schedule/${EMPRESA_SLUG}`, {
              method:"POST", headers:{"Content-Type":"application/json"},
              body: JSON.stringify(nuevoSchedule),
            }).catch(() => {});
          }} style={{
            padding:"6px 16px", borderRadius:20, border:"none", cursor:"pointer", fontSize:12, fontWeight:600,
            background: schedule.envio_automatico ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)",
            color: schedule.envio_automatico ? "#10B981" : "#EF4444",
          }}>
            {schedule.envio_automatico ? "● Activado" : "○ Desactivado"}
          </button>
        </div>
      )}

      <div style={{ display:"flex", gap:4, marginBottom:20, borderBottom:"1px solid rgba(var(--brand-ink-rgb),0.1)", paddingBottom:0 }}>
        {subtabs.map(st => (
          <button key={st.id} onClick={() => setSubTab(st.id)} style={{
            padding:"10px 20px", fontSize:13, fontWeight: subTab === st.id ? 700 : 500, cursor:"pointer",
            background:"none", border:"none", borderBottom: subTab === st.id ? "2px solid var(--brand-ink)" : "2px solid transparent",
            color: subTab === st.id ? "var(--brand-ink)" : "#888884",
            marginBottom:-1,
          }}>{st.label}</button>
        ))}
      </div>

      {subTab === "seguimiento" && <TabSeguimiento leads={leads} />}
      {subTab === "plantillas" && <TabEmailEditor pin={pin} />}
      {subTab === "salud" && <TabEmailHealth />}
    </div>
  );
}

function TabEmailEditor({ pin }) {
  const [templates, setTemplates]         = useState(null); // null = cargando
  const [seleccionado, setSeleccionado]   = useState(null);
  const [editando, setEditando]           = useState(null);
  const [vista, setVista]                 = useState("editor");
  const [guardado, setGuardado]           = useState(false);
  const [guardando, setGuardando]         = useState(false);
  const [generando, setGenerando]         = useState(false);
  const [brief, setBrief]                 = useState("");
  const [errorCarga, setErrorCarga]       = useState(false);
  const branding = useBranding();

  // Cargar templates desde el servidor al montar
  useEffect(() => {
    fetch(`/api/templates/${EMPRESA_SLUG}`)
      .then(r => r.json())
      .then(data => {
        const arr = Array.isArray(data) && data.length > 0 ? data : getTemplatesDefault(branding);
        setTemplates(arr);
        setSeleccionado(arr[0]?.id || null);
      })
      .catch(() => {
        // Sin servidor — usar defaults
        setTemplates(getTemplatesDefault(branding));
        setSeleccionado(getTemplatesDefault(branding)[0]?.id || null);
        setErrorCarga(true);
      });
  }, []);

  // Guardar array completo en el servidor
  const persistir = async (arr) => {
    setGuardando(true);
    try {
      await fetch(`/api/templates/${EMPRESA_SLUG}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(arr),
      });
    } catch(e) {
      console.warn("No se pudo guardar en servidor, solo en memoria");
    }
    setGuardando(false);
  };

  const tpl = editando || (templates || []).find(t => t.id === seleccionado);

  const seleccionar = (id) => {
    setSeleccionado(id);
    setEditando(null);
    setVista("editor");
    setGuardado(false);
  };

  const cambiarHtml = (html) => {
    setEditando({ ...tpl, html });
    setGuardado(false);
  };

  const cambiarNombre = (nombre) => {
    setEditando({ ...tpl, nombre });
    setGuardado(false);
  };

  const cambiarTipo = (tipo) => {
    setEditando({ ...tpl, tipo });
    setGuardado(false);
  };

  const cambiarAsunto = (asunto) => {
    setEditando({ ...tpl, asunto });
    setGuardado(false);
  };

  const guardar = async () => {
    const updated = templates.map(t => t.id === tpl.id ? tpl : t);
    await persistir(updated);
    setTemplates(updated);
    setEditando(null);
    setGuardado(true);
    setTimeout(() => setGuardado(false), 2500);
  };

  const descartar = () => {
    setEditando(null);
    setGuardado(false);
  };

  const eliminarTemplate = async (id) => {
    if (!window.confirm("¿Eliminar este template?")) return;
    const updated = templates.filter(t => t.id !== id);
    await persistir(updated);
    setTemplates(updated);
    setSeleccionado(updated[0]?.id || null);
    setEditando(null);
  };

  const generarConIA = async () => {
    if (!brief.trim()) return;
    setGenerando(true);
    try {
      // OJO: igual que en QZ/AARTI/FL, NUNCA llamar a api.anthropic.com directo
      // desde el navegador (CORS) — siempre pasar por el proxy del server.
      const prompt = `Eres un experto en email marketing B2B para ${branding.nombre}${branding.tagline ? ` (${branding.tagline})` : ""}.

Genera SOLO el HTML interno de un email de ventas en español basado en este brief:
"${brief}"

REGLAS ESTRICTAS:
- Solo devuelve el HTML de la tabla, sin <!DOCTYPE>, sin <html>, sin <head>, sin explicación
- Usa tablas HTML con estilos inline (compatible con Gmail)
- Paleta: fondo ${branding.colorSecundario}, texto ${branding.colorPrimario}, gris #444440, acento sutil #888884 — siempre como color hex, nunca variables CSS
- Tipografía Georgia serif
- Estructura: cabecera ${branding.nombre} → cuerpo del mensaje → CTA con link wa.me → firma ${branding.firma} → footer
- CTA: botón de tabla con background ${branding.colorPrimario}, texto ${branding.colorSecundario}, en cursiva
- Tono: elegante, cercano, sin exceso de exclamaciones, sin emojis
- Variables permitidas: {{contacto}}, {{empresa}}
- El HTML debe empezar con <table y terminar con </table>`;

      const resp = await fetch(`/api/ai/generar-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt, pin }),
      });
      const data = await resp.json();
      const html = data.html || data.text || data.content?.[0]?.text || "";
      const limpio = html.replace(/```html?/gi, "").replace(/```/g, "").trim();
      cambiarHtml(limpio);
      setBrief("");
    } catch (e) {
      alert("Error al conectar con la IA");
    }
    setGenerando(false);
  };

  const tipoColor = { presentacion:"#3B82F6", seguimiento:"#F59E0B", propuesta:"#10B981", custom:"#8B5CF6" };

  // Cargando
  if (templates === null) return (
    <div style={{ padding:60, textAlign:"center", color:"rgba(var(--brand-ink-rgb),0.3)", fontSize:13 }}>
      Cargando templates...
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20, flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ fontWeight:800, fontSize:18, color:"var(--brand-ink)", marginBottom:3 }}>✉️ Editor de emails</div>
          <div style={{ fontSize:12, color:"rgba(var(--brand-ink-rgb),0.4)" }}>Creá y editá los templates. Asigná el tipo para que Hermes los use automáticamente.</div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          {guardando && <span style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.4)", fontStyle:"italic" }}>Guardando...</span>}
          {errorCarga && (
            <span style={{ fontSize:11, color:"#F59E0B", background:"rgba(245,158,11,0.1)", padding:"4px 10px", borderRadius:6 }}>
              ⚠️ Sin servidor — cambios solo en memoria
            </span>
          )}
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"260px 1fr", gap:16, alignItems:"start" }}>

        {/* Sidebar — lista de templates */}
        <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, overflow:"hidden" }}>
          <div style={{ padding:"12px 16px", borderBottom:"1px solid rgba(var(--brand-ink-rgb),0.07)" }}>
            <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:"0.1em", color:"rgba(var(--brand-ink-rgb),0.4)" }}>Templates ({templates.length})</div>
          </div>
          {templates.map(t => (
            <div
              key={t.id}
              onClick={() => seleccionar(t.id)}
              style={{
                padding:"13px 16px",
                background: seleccionado === t.id ? "rgba(var(--brand-ink-rgb),0.06)" : "transparent",
                borderLeft: seleccionado === t.id ? "3px solid var(--brand-ink)" : "3px solid transparent",
                cursor:"pointer",
                borderBottom:"1px solid rgba(var(--brand-ink-rgb),0.05)",
                transition:"background 0.1s",
              }}
            >
              <div style={{ fontSize:13, fontWeight: seleccionado===t.id ? 700 : 500, color:"var(--brand-ink)", marginBottom:4 }}>{t.nombre}</div>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <span style={{
                  fontSize:9, fontWeight:700, padding:"2px 8px", borderRadius:10,
                  background:`${tipoColor[t.tipo]||"#6B7280"}18`,
                  color: tipoColor[t.tipo] || "#6B7280",
                }}>{TIPOS_EMAIL.find(x=>x.id===t.tipo)?.label || t.tipo}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Panel principal */}
        {tpl ? (
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>

            {/* Barra superior */}
            <div style={{
              background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)",
              borderRadius:12, padding:"14px 18px",
              display:"flex", alignItems:"center", gap:12, flexWrap:"wrap",
            }}>
              {/* Nombre editable */}
              <input
                value={tpl.nombre}
                onChange={e => cambiarNombre(e.target.value)}
                style={{
                  flex:1, minWidth:180, background:"transparent",
                  border:"none", borderBottom:"1px solid rgba(var(--brand-ink-rgb),0.15)",
                  color:"var(--brand-ink)", fontSize:15, fontWeight:700, padding:"2px 0",
                  outline:"none", fontFamily:"Georgia,serif",
                }}
              />

              {/* Tipo — fijo para los 7 del flujo, editable solo en templates personalizados */}
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.4)", whiteSpace:"nowrap" }}>Tipo Hermes:</span>
                {TIPOS_EMAIL.some(t => t.id === tpl.id) ? (
                  <span style={{
                    background:"rgba(var(--brand-ink-rgb),0.06)", border:"1px solid rgba(var(--brand-ink-rgb),0.1)",
                    borderRadius:7, padding:"6px 12px", color:"var(--brand-ink)",
                    fontSize:12, fontWeight:600,
                  }}>{TIPOS_EMAIL.find(t => t.id === tpl.tipo)?.label || tpl.tipo}</span>
                ) : (
                  <select
                    value={tpl.tipo}
                    onChange={e => cambiarTipo(e.target.value)}
                    style={{
                      background:"#fff", border:"1px solid rgba(var(--brand-ink-rgb),0.15)",
                      borderRadius:7, padding:"6px 12px", color:"var(--brand-ink)",
                      fontSize:12, outline:"none", fontWeight:600,
                    }}
                  >
                    {TIPOS_EMAIL.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                )}
              </div>

              {/* Asunto del email */}
              <div style={{ display:"flex", alignItems:"center", gap:8, flex:"1 0 100%" }}>
                <span style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.4)", whiteSpace:"nowrap" }}>Asunto:</span>
                <input
                  value={tpl.asunto||""}
                  onChange={e => cambiarAsunto(e.target.value)}
                  placeholder="Ej: Presentamos [TuEmpresa] — Servicios para {{empresa}}"
                  style={{
                    flex:1, background:"transparent",
                    border:"none", borderBottom:"1px solid rgba(var(--brand-ink-rgb),0.15)",
                    color:"var(--brand-ink)", fontSize:13, padding:"2px 0",
                    outline:"none", fontFamily:"Georgia,serif",
                  }}
                />
              </div>

              {/* Vista preview / editor */}
              <div style={{ display:"flex", background:"rgba(var(--brand-ink-rgb),0.06)", borderRadius:7, overflow:"hidden" }}>
                {["editor","preview"].map(v => (
                  <button key={v} onClick={() => setVista(v)} style={{
                    padding:"6px 14px", border:"none", cursor:"pointer",
                    background: vista===v ? "var(--brand-ink)" : "transparent",
                    color: vista===v ? "var(--brand-paper)" : "rgba(var(--brand-ink-rgb),0.5)",
                    fontSize:11, fontWeight:600, transition:"all 0.1s",
                  }}>{v === "editor" ? "✏️ Código" : "👁 Vista previa"}</button>
                ))}
              </div>

              {/* Guardar / descartar */}
              {editando && (
                <div style={{ display:"flex", gap:6 }}>
                  <button onClick={descartar} style={{
                    padding:"6px 14px", borderRadius:7, border:"1px solid rgba(var(--brand-ink-rgb),0.12)",
                    background:"transparent", color:"rgba(var(--brand-ink-rgb),0.5)", cursor:"pointer", fontSize:12,
                  }}>Descartar</button>
                  <button onClick={guardar} disabled={guardando} style={{
                    padding:"6px 16px", borderRadius:7, border:"none",
                    background: guardando ? "rgba(var(--brand-ink-rgb),0.4)" : "var(--brand-ink)",
                    color:"var(--brand-paper)", fontWeight:700, cursor: guardando ? "not-allowed" : "pointer", fontSize:12,
                  }}>{guardando ? "Guardando..." : "💾 Guardar"}</button>
                </div>
              )}
              {guardado && (
                <span style={{ fontSize:12, color:"#10B981", fontWeight:600 }}>✓ Guardado</span>
              )}

              {/* Eliminar */}
              {!getTemplatesDefault(branding).find(d => d.id === tpl.id) && (
                <button onClick={() => eliminarTemplate(tpl.id)} style={{
                  padding:"6px 10px", borderRadius:7, border:"1px solid rgba(239,68,68,0.2)",
                  background:"rgba(239,68,68,0.07)", color:"#EF4444", cursor:"pointer", fontSize:11,
                }}>🗑</button>
              )}
            </div>

            {/* Editor HTML */}
            {vista === "editor" && (
              <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:12, overflow:"hidden" }}>
                <div style={{ padding:"10px 16px", borderBottom:"1px solid rgba(var(--brand-ink-rgb),0.07)", display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.4)", textTransform:"uppercase", letterSpacing:"0.1em" }}>HTML del email</span>
                  <span style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.25)" }}>· Gmail-compatible · estilos inline · tablas</span>
                </div>
                <textarea
                  value={tpl.html}
                  onChange={e => cambiarHtml(e.target.value)}
                  spellCheck={false}
                  style={{
                    width:"100%", height:360, background:"rgba(0,0,0,0.02)",
                    border:"none", padding:"16px 18px",
                    color:"var(--brand-ink)", fontSize:12, fontFamily:"'Courier New',monospace",
                    resize:"vertical", outline:"none", boxSizing:"border-box",
                    lineHeight:1.6,
                  }}
                />
              </div>
            )}

            {/* Vista previa */}
            {vista === "preview" && (
              <div style={{ background:"#e8e4de", borderRadius:12, padding:24, overflow:"hidden" }}>
                <div style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.4)", marginBottom:12, textTransform:"uppercase", letterSpacing:"0.1em" }}>
                  Vista previa — se verá similar en Gmail
                </div>
                <iframe
                  srcDoc={`<!DOCTYPE html><html><body style="margin:0;padding:0;background:#e8e4de;">${tpl.html}</body></html>`}
                  style={{ width:"100%", border:"none", borderRadius:8, minHeight:420, background:"transparent" }}
                  title="preview"
                  sandbox="allow-same-origin"
                />
              </div>
            )}

            {/* Generador IA */}
            <div style={{
              background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)",
              borderRadius:12, padding:18,
            }}>
              <div style={{ fontSize:13, fontWeight:700, color:"var(--brand-ink)", marginBottom:4 }}>✨ Generador con IA</div>
              <div style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.4)", marginBottom:12 }}>
                Describí el email que necesitás y DeepSeek lo genera respetando la identidad de {branding.nombre}.
              </div>
              <div style={{ display:"flex", gap:10 }}>
                <textarea
                  value={brief}
                  onChange={e => setBrief(e.target.value)}
                  placeholder='ej: "Email para empresas tecnológicas de Madrid, invitándolas a un taller de 5h sobre liderazgo y presencia. Mencionar beneficios concretos para equipos de más de 50 personas."'
                  style={{
                    flex:1, background:"#fff", border:"1px solid rgba(var(--brand-ink-rgb),0.15)",
                    borderRadius:8, padding:"10px 14px", color:"var(--brand-ink)", fontSize:12,
                    resize:"none", outline:"none", minHeight:72, fontFamily:"Georgia,serif",
                    lineHeight:1.6,
                  }}
                />
                <button
                  onClick={generarConIA}
                  disabled={generando || !brief.trim()}
                  style={{
                    padding:"10px 20px", borderRadius:8, border:"none",
                    background: generando || !brief.trim() ? "rgba(var(--brand-ink-rgb),0.1)" : "var(--brand-ink)",
                    color: generando || !brief.trim() ? "rgba(var(--brand-ink-rgb),0.3)" : "var(--brand-paper)",
                    fontWeight:700, fontSize:12, cursor: generando || !brief.trim() ? "not-allowed" : "pointer",
                    whiteSpace:"nowrap", alignSelf:"stretch", fontFamily:"Georgia,serif", fontStyle:"italic",
                  }}
                >
                  {generando ? "⏳ Generando..." : "✨ Generar\ncon IA"}
                </button>
              </div>
              {generando && (
                <div style={{ marginTop:10, fontSize:11, color:"rgba(var(--brand-ink-rgb),0.4)", fontStyle:"italic" }}>
                  La IA está generando el email... puede tardar unos segundos.
                </div>
              )}
            </div>

            {/* Info tipo asignado */}
            <div style={{
              background:`${tipoColor[tpl.tipo]||"#6B7280"}0d`,
              border:`1px solid ${tipoColor[tpl.tipo]||"#6B7280"}25`,
              borderRadius:10, padding:"12px 16px",
            }}>
              <div style={{ fontSize:12, fontWeight:700, color: tipoColor[tpl.tipo]||"#6B7280", marginBottom:4 }}>
                📨 Este template está asignado como: <strong>{TIPOS_EMAIL.find(x=>x.id===tpl.tipo)?.label || tpl.tipo}</strong>
              </div>
              <div style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.5)" }}>
                Cuando Hermes envíe un email de tipo <code style={{ background:"rgba(0,0,0,0.06)", padding:"1px 6px", borderRadius:4 }}>{tpl.tipo}</code> para {branding.nombre}, usará este template.
                Para activarlo en el servidor, copiá el HTML y pegalo en <code style={{ background:"rgba(0,0,0,0.06)", padding:"1px 6px", borderRadius:4 }}>templates/aarti/email_{tpl.tipo}.html</code>
              </div>
            </div>

          </div>
        ) : (
          <div style={{ padding:40, textAlign:"center", color:"rgba(var(--brand-ink-rgb),0.3)", fontSize:13 }}>
            Seleccioná un template para editarlo
          </div>
        )}

      </div>
    </div>
  );
}

// ── TAB LEADS ────────────────────────────────────────────────────
function TabLeads({ leads }) {
  const [busqueda, setBusqueda]       = useState("");
  const [filtroEstado, setFiltroEstado] = useState("All");
  const [filtroRegion, setFiltroRegion] = useState("All");
  const [sortCol, setSortCol]         = useState("score");
  const [sortDir, setSortDir]         = useState("desc");
  const [importando, setImportando]   = useState(false);
  const [importMsg, setImportMsg]     = useState(null);

  const descargarTemplate = () => {
    const cols = ["empresa","contacto","email","fuente","segmento","ciudad"];
    const ejemplo = ["Acme S.L.","Juan García","j.garcia@acme.com","Base propia","Tecnología","Madrid"];
    const rows = [cols, ejemplo];
    const csv = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob(["﻿" + csv], { type:"text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = "template_importar_leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const importarCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportando(true);
    setImportMsg(null);

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const texto = ev.target.result;
        const lineas = texto.split("\n").map(l => l.trim()).filter(Boolean);
        if (lineas.length < 2) throw new Error("El archivo está vacío o solo tiene el header");

        const sep = lineas[0].includes(";") ? ";" : ",";
        const header = lineas[0].split(sep).map(h => h.replace(/"/g,"").trim().toLowerCase());
        const iEmpresa  = header.indexOf("empresa");
        const iContacto = header.indexOf("contacto");
        const iEmail    = header.indexOf("email");
        const iFuente   = header.indexOf("fuente");
        const iSegmento = header.indexOf("segmento");
        const iCiudad   = header.indexOf("ciudad");

        if (iEmpresa === -1 || iEmail === -1) {
          throw new Error("El archivo debe tener al menos las columnas 'empresa' y 'email'");
        }

        const parsearFila = (linea) => {
          const vals = [];
          let dentro = false, actual = "";
          for (const c of linea) {
            if (c === '"') { dentro = !dentro; }
            else if ((c === sep) && !dentro) { vals.push(actual.trim()); actual = ""; }
            else actual += c;
          }
          vals.push(actual.trim());
          return vals;
        };

        const leads_nuevos = lineas.slice(1).map((linea) => {
          const vals = parsearFila(linea);
          const empresa = vals[iEmpresa] || "";
          const email   = vals[iEmail]   || "";
          if (!empresa || !email) return null;
          return {
            empresa,
            contacto:  iContacto >= 0 ? vals[iContacto] || "" : "",
            email_estimado: email,
            fuente:    iFuente   >= 0 ? vals[iFuente]   || "Importación manual" : "Importación manual",
            segmento:  iSegmento >= 0 ? vals[iSegmento] || "" : "",
            ciudad:    iCiudad   >= 0 ? vals[iCiudad]   || "" : "",
            estado:    "Pendiente",
            score:     0,
            fecha:     new Date().toISOString().split("T")[0],
          };
        }).filter(Boolean);

        if (leads_nuevos.length === 0) {
          throw new Error("No se encontraron filas válidas (verificá que tengan empresa y email)");
        }

        fetch(`/api/importar-leads`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ empresa: EMPRESA_SLUG, leads: leads_nuevos }),
        })
        .then(r => r.json())
        .then(d => {
          setImportando(false);
          if (d.ok) {
            setImportMsg({ ok: true, texto: `${d.importados} leads importados correctamente. Nexus los calificará en la próxima corrida.` });
          } else {
            setImportMsg({ ok: false, texto: `Error: ${d.error}` });
          }
        })
        .catch(() => {
          setImportando(false);
          setImportMsg({ ok: false, texto: "Error de conexión al importar" });
        });

      } catch (err) {
        setImportando(false);
        setImportMsg({ ok: false, texto: err.message });
      }
    };
    reader.readAsText(file, "utf-8");
    e.target.value = "";
  };

  const exportCSV = () => {
    const cols = ["ID","Company","Contact","Role","Email","Phone","City","Market","Segment","Source","Status","Score","Amount","Date","Notes"];
    const rows = leadsFiltrados.map(l => [
      l.id, l.empresa, l.contacto, l.cargo, l.email, l.tel,
      l.ciudad, l.region, l.segmento, l.fuente, l.estado,
      l.score, l.monto, l.fecha, l.notas,
    ]);
    const csv = [cols, ...rows]
      .map(row => row.map(v => '"' + String(v||"").replace(/"/g, '""') + '"').join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type:"text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `leads_${EMPRESA_SLUG}_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const estados  = ["All", ...Array.from(new Set(leads.map(l=>l.estado).filter(Boolean)))];
  const regiones = ["All", ...Array.from(new Set(leads.map(l=>l.region).filter(Boolean)))];

  const leadsFiltrados = leads
    .filter(l => {
      if (filtroEstado !== "All" && l.estado !== filtroEstado) return false;
      if (filtroRegion !== "All" && l.region !== filtroRegion) return false;
      if (busqueda && !`${l.empresa}${l.contacto}${l.email}${l.segmento}`.toLowerCase().includes(busqueda.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      const va = a[sortCol] ?? "";
      const vb = b[sortCol] ?? "";
      if (typeof va === "number") return sortDir === "asc" ? va - vb : vb - va;
      return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });

  const toggleSort = col => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
  };

  const thStyle = col => ({
    padding:"10px 12px", textAlign:"left", fontSize:11, fontWeight:700,
    color: sortCol === col ? C.gold : C.navyLite,
    textTransform:"uppercase", letterSpacing:"0.07em", cursor:"pointer",
    borderBottom:`2px solid ${sortCol === col ? C.gold : C.rule}`,
    whiteSpace:"nowrap", userSelect:"none", background:"#fff",
  });

  const COLS = [
    { key:"empresa",  label:"Company"  },
    { key:"contacto", label:"Contact"  },
    { key:"cargo",    label:"Cargo"    },
    { key:"email",    label:"Email"    },
    { key:"tel",      label:"Teléfono" },
    { key:"region",   label:"Market"   },
    { key:"segmento", label:"Segment"  },
    { key:"fuente",   label:"Source"   },
    { key:"estado",   label:"Status"   },
    { key:"score",    label:"Score"    },
    { key:"monto",    label:"Amount"   },
    { key:"fecha",    label:"Date"     },
  ];

  const estadoColor = {
    Nuevo:"#6B7A94", Validado:"#2D4470", Cotizado:C.gold,
    Ganado:"#10B981", Perdido:"#EF4444"
  };

  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20, flexWrap:"wrap" }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:18, fontWeight:800, color:C.navy }}>📋 Leads</div>
          <div style={{ fontSize:12, color:C.navyLite, marginTop:2 }}>{leadsFiltrados.length} of {leads.length} leads</div>
        </div>
        <button onClick={descargarTemplate}
          style={{ padding:"9px 20px", borderRadius:8, border:`1px solid ${C.gold}`, background:`${C.gold}15`, color:C.gold, fontWeight:700, fontSize:13, cursor:"pointer" }}>
          Template CSV
        </button>
        <label style={{ padding:"9px 20px", borderRadius:8, border:`1px solid ${C.gold}`, background:importando ? `${C.gold}30` : `${C.gold}15`, color:C.gold, fontWeight:700, fontSize:13, cursor:"pointer", opacity:importando?0.6:1 }}>
          {importando ? "Importando..." : "Importar CSV"}
          <input type="file" accept=".csv,.txt" onChange={importarCSV} style={{ display:"none" }} disabled={importando} />
        </label>
        <button onClick={exportCSV}
          style={{ padding:"9px 20px", borderRadius:8, border:`1px solid ${C.gold}`, background:`${C.gold}15`, color:C.gold, fontWeight:700, fontSize:13, cursor:"pointer" }}>
          Export CSV
        </button>
      </div>
      {importMsg && (
        <div style={{ marginBottom:14, padding:"10px 16px", borderRadius:8, fontSize:13, fontWeight:600,
          background: importMsg.ok ? "#10B98120" : "#EF444420",
          color: importMsg.ok ? "#10B981" : "#EF4444",
          border: `1px solid ${importMsg.ok ? "#10B981" : "#EF4444"}` }}>
          {importMsg.texto}
        </div>
      )}

      <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap" }}>
        <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Search company, contact, email..."
          style={{ flex:1, minWidth:200, background:"#fff", border:`1px solid ${C.rule}`, borderRadius:8, padding:"8px 14px", color:C.navy, fontSize:13, outline:"none" }}/>
        <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}
          style={{ background:"#fff", border:`1px solid ${C.rule}`, borderRadius:8, padding:"8px 12px", color:C.navy, fontSize:13, outline:"none" }}>
          {estados.map(e => <option key={e} value={e}>{e === "All" ? "All statuses" : e}</option>)}
        </select>
        <select value={filtroRegion} onChange={e => setFiltroRegion(e.target.value)}
          style={{ background:"#fff", border:`1px solid ${C.rule}`, borderRadius:8, padding:"8px 12px", color:C.navy, fontSize:13, outline:"none" }}>
          {regiones.map(r => <option key={r} value={r}>{r === "All" ? "All markets" : r}</option>)}
        </select>
      </div>

      <div style={{ background:"#fff", border:`1px solid rgba(26,43,74,0.09)`, borderRadius:14, overflow:"hidden" }}>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr>
                {COLS.map(col => (
                  <th key={col.key} onClick={() => toggleSort(col.key)} style={thStyle(col.key)}>
                    {col.label} {sortCol === col.key ? (sortDir === "asc" ? "↑" : "↓") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leadsFiltrados.length === 0 && (
                <tr><td colSpan={COLS.length} style={{ padding:40, textAlign:"center", color:C.navyLite, fontSize:13 }}>No leads found</td></tr>
              )}
              {leadsFiltrados.map((lead, i) => (
                <tr key={lead.id}
                  style={{ borderTop:`1px solid rgba(26,43,74,0.05)`, background: i%2===0 ? "#fff" : "#FAFAF9", transition:"background 0.1s" }}
                  onMouseEnter={e => e.currentTarget.style.background = `${C.gold}08`}
                  onMouseLeave={e => e.currentTarget.style.background = i%2===0 ? "#fff" : "#FAFAF9"}>
                  <td style={{ padding:"10px 12px", color:C.navy, fontWeight:600, maxWidth:180, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{lead.empresa}</td>
                  <td style={{ padding:"10px 12px", color:C.navyMid, fontWeight:500 }}>
                    {lead.contacto}
                    {lead.url_linkedin && <a href={lead.url_linkedin} target="_blank" rel="noreferrer" style={{ marginLeft:6, fontSize:10, color:"#0077B5", textDecoration:"none" }}>in</a>}
                  </td>
                  <td style={{ padding:"10px 12px", color:C.navyLite, fontSize:12 }}>{lead.cargo}</td>
                  <td style={{ padding:"10px 12px", color:C.navyLite, fontSize:12 }}>
                    {lead.email}
                    {lead.email && lead.email_verificado && <span title="Verificado: coincide con el decisor confirmado en LinkedIn" style={{ marginLeft:5, color:"#10B981" }}>✓</span>}
                    {lead.contactos_hunter.length > 1 && (
                      <span
                        title={lead.contactos_hunter.map(c => `${c.email}${c.nombre ? " — " + c.nombre : ""}`).join("\n")}
                        style={{ marginLeft:5, fontSize:9, fontWeight:700, padding:"1px 6px", borderRadius:10, background:`${C.gold}18`, color:C.gold, cursor:"help" }}
                      >+{lead.contactos_hunter.length - 1} más</span>
                    )}
                  </td>
                  <td style={{ padding:"10px 12px", color:C.navyLite, fontSize:12, whiteSpace:"nowrap" }}>{lead.tel}</td>
                  <td style={{ padding:"10px 12px" }}>
                    <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:10, background:`${C.gold}18`, color:C.gold, whiteSpace:"nowrap" }}>{lead.region}</span>
                  </td>
                  <td style={{ padding:"10px 12px", color:C.navyLite, fontSize:12 }}>{lead.segmento}</td>
                  <td style={{ padding:"10px 12px", color:C.navyLite, fontSize:12 }}>{lead.fuente}</td>
                  <td style={{ padding:"10px 12px" }}>
                    <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:10, background:`${estadoColor[lead.estado]||"#6B7280"}18`, color:estadoColor[lead.estado]||"#6B7280", whiteSpace:"nowrap" }}>{lead.estado}</span>
                  </td>
                  <td style={{ padding:"10px 12px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <div style={{ width:36, height:4, background:"rgba(26,43,74,0.08)", borderRadius:2, overflow:"hidden" }}>
                        <div style={{ width:`${lead.score}%`, height:"100%", background:scoreColor(lead.score), borderRadius:2 }}/>
                      </div>
                      <span style={{ fontSize:11, fontWeight:700, color:scoreColor(lead.score) }}>{lead.score}</span>
                    </div>
                  </td>
                  <td style={{ padding:"10px 12px", color:C.gold, fontWeight:700, fontSize:12, whiteSpace:"nowrap" }}>{lead.monto > 0 ? fmt$(lead.monto) : "—"}</td>
                  <td style={{ padding:"10px 12px", color:C.navyLite, fontSize:11, whiteSpace:"nowrap" }}>{lead.fecha}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {leadsFiltrados.length > 0 && (
          <div style={{ padding:"12px 16px", borderTop:`1px solid rgba(26,43,74,0.06)`, display:"flex", gap:24, flexWrap:"wrap" }}>
            <span style={{ fontSize:11, color:C.navyLite }}>
              <strong style={{ color:C.navy }}>{leadsFiltrados.length}</strong> leads
            </span>
            <span style={{ fontSize:11, color:C.navyLite }}>
              Avg score: <strong style={{ color:C.navy }}>{Math.round(leadsFiltrados.reduce((a,b)=>a+b.score,0)/leadsFiltrados.length)}</strong>
            </span>
            {leadsFiltrados.some(l=>l.monto>0) && (
              <span style={{ fontSize:11, color:C.navyLite }}>
                Total: <strong style={{ color:C.gold }}>{fmt$(leadsFiltrados.filter(l=>l.monto>0).reduce((a,b)=>a+b.monto,0))}</strong>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// ── APP PRINCIPAL ────────────────────────────────────────────────
export default function App() {
  // ── Estados (TODOS primero) ──────────────────────────────────
  const isMobile = useIsMobile();
  const [usuario, setUsuario]           = useLocalStorage("captio_usuario", null);
  const [leads, setLeads]               = useLocalStorage("captio_leads", LEADS_DEMO);
  const [regiones, setRegiones]         = useLocalStorage("captio_regiones", REGIONES_INICIALES);
  const [tab, setTab]                   = useState("dashboard");
  const [filtroEmpresa, setFiltroEmpresa]     = useState("Todas");
  const [filtroRegion, setFiltroRegion]       = useState("Todas");
  const [filtroSegmento, setFiltroSegmento]   = useState("Todos");
  const [filtroVendedor, setFiltroVendedor]   = useState("Todos");
  const [busqueda, setBusqueda]         = useState("");
  const [dragId, setDragId]             = useState(null);
  const [modal, setModal]               = useState(null);
  const [configs, setConfigs]           = useState({});
  const [configEmpresa, setConfigEmpresa]     = useState(EMPRESA_SLUG);
  const [configEditando, setConfigEditando]   = useState(null);
  const [pendientes, setPendientes]     = useState({});
  const [sysStatus, setSysStatus]       = useState(null);
  const [smtpConfig, setSmtpConfig]     = useState({ email:"", password:"", host:"", port:"", modo:"" });
  const [smtpStatus, setSmtpStatus]     = useState(null); // null | "ok" | "error" | "testing" | "saving"
  const [schedule, setSchedule]         = useState({ dias_busqueda:[1,3], dias_envio:[2,4], hora_envio:"09:00", sistema_activo:false });
  const [vendedores, setVendedores]     = useState(VENDEDORES);
  const [knowledgeVersion, setKnowledgeVersion] = useState(0);
  const [nuevoVendedorNombre, setNuevoVendedorNombre] = useState("");
  const [estadoSistema, setEstadoSistema] = useState(null);
  const [emailInbox, setEmailInbox] = useState({ activo:false, email:"", password:"", host:"", port:993, tls:true, filtro_remitente:"", filtro_asunto:"", frecuencia_min:30 });
  const [inboxStatus, setInboxStatus] = useState(null);
  const branding = useBranding();

  // ── Constantes ───────────────────────────────────────────────
  const esAdmin = usuario?.rol === "admin";

  // Cargar usuarios (vendedores) SIN depender del login — el propio
  // Login los necesita para decidir si pide PIN o entra directo.
  useEffect(() => {
    fetch(`/api/vendedores/${EMPRESA_SLUG}`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data) && data.length > 0) setVendedores(data); })
      .catch(() => {});
  }, []);

  // ── Hooks de datos ───────────────────────────────────────────
  useLeadsFromFile(setLeads);

  useEffect(() => {
    if (!esAdmin) return;
    const empresas = [EMPRESA_SLUG];
    empresas.forEach(emp => {
      fetch(`/api/config/${emp}`)
        .then(r => r.json())
        .then(data => setConfigs(prev => ({ ...prev, [emp]: data })))
        .catch(() => {});
      fetch(`/api/pendientes/${emp}`)
        .then(r => r.json())
        .then(data => setPendientes(prev => ({ ...prev, [emp]: data.pendientes || 0 })))
        .catch(() => {});
      fetch(`/api/smtp/${emp}`)
        .then(r => r.json())
        .then(data => { if (data.email) setSmtpConfig(data); })
        .catch(() => {});
      fetch(`/api/schedule/${emp}`)
        .then(r => r.json())
        .then(data => setSchedule(data))
        .catch(() => {});
      fetch(`/api/estado-sistema/${emp}`)
        .then(r => r.json())
        .then(data => setEstadoSistema(data))
        .catch(() => {});
      fetch(`/api/email-inbox/${emp}`)
        .then(r => r.json())
        .then(data => setEmailInbox(prev => ({ ...prev, ...data })))
        .catch(() => {});
    });
  }, [esAdmin]);

  useEffect(() => {
    const fetchStatus = () => {
      fetch("/api/status")
        .then(r => r.json())
        .then(data => setSysStatus(data))
        .catch(() => {});
    };
    fetchStatus();
    const iv = setInterval(fetchStatus, 2 * 60 * 1000);
    return () => clearInterval(iv);
  }, []);

  // Filtrar leads según rol y filtros
  const leadsFiltrados = leads.filter(l => {
    if (!esAdmin && l.vendedor !== usuario?.id) return false;
    if (filtroEmpresa !== "Todas" && l.empresa_captio !== filtroEmpresa) return false;
    if (filtroRegion !== "Todas" && l.region !== filtroRegion) return false;
    if (filtroSegmento !== "Todos" && l.segmento !== filtroSegmento) return false;
    if (filtroVendedor !== "Todos" && l.vendedor !== filtroVendedor) return false;
    if (busqueda) {
      const q = busqueda.toLowerCase();
      return l.empresa.toLowerCase().includes(q) || l.contacto.toLowerCase().includes(q);
    }
    return true;
  });

  // KPIs
  const pipeline    = leads.filter(l=>l.estado==="Cotizado").reduce((a,b)=>a+b.monto,0);
  const ganado      = leads.filter(l=>l.estado==="Ganado").reduce((a,b)=>a+b.monto,0);
  const totalCerr   = leads.filter(l=>l.estado==="Ganado"||l.estado==="Perdido").length;
  const tasaCierre  = totalCerr ? Math.round(leads.filter(l=>l.estado==="Ganado").length/totalCerr*100) : 0;
  const nuevosHoy   = leads.filter(l=>l.fecha===new Date().toISOString().split("T")[0]).length;
  const leadsGanados = leads.filter(l=>l.estado==="Ganado"&&l.monto>0);
  const valorPromedio = leadsGanados.length ? Math.round(leadsGanados.reduce((a,b)=>a+b.monto,0)/leadsGanados.length) : 0;
  const leadsValidados = leads.filter(l=>l.estado==="Validado").length;

  // Drag & Drop
  const handleDragStart = useCallback((e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDragOver = useCallback(e => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }, []);

  const handleDrop = useCallback((e, colId) => {
    e.preventDefault();
    if (!dragId) return;
    const lead = leads.find(l => l.id === dragId);
    if (!lead || lead.estado === colId) { setDragId(null); return; }
    setModal({ tipo:"mover", lead, destino:colId });
    setDragId(null);
  }, [dragId, leads]);

  const confirmarMover = ({ monto, motivo }) => {
    const { lead, destino } = modal;
    const nuevosLeads = leads.map(l =>
      l.id === lead.id
        ? { ...l, estado:destino, monto: monto || l.monto, notas: motivo ? `Perdido: ${motivo}` : l.notas }
        : l
    );
    setLeads(nuevosLeads);
    // Persistir en el servidor
    fetch("/api/leads", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(nuevosLeads.map(l => ({
        ...l,
        cargo_decisor: l.cargo,
        email_estimado: l.email,
        telefono: l.tel,
      })))
    }).catch(() => {});
    setModal(null);
  };

  const asignarVendedor = (leadId, vendedorId) => {
    setLeads(prev => prev.map(l => l.id===leadId ? {...l, vendedor:vendedorId} : l));
  };

  // Mover un lead a Cotizado directamente desde la ficha (al enviar el presupuesto)
  const moverACotizado = (leadId, monto) => {
    setLeads(prev => {
      const nuevosLeads = prev.map(l =>
        l.id === leadId ? { ...l, estado:"Cotizado", monto: monto || l.monto } : l
      );
      fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(nuevosLeads.map(l => ({
          ...l,
          cargo_decisor: l.cargo,
          email_estimado: l.email,
          telefono: l.tel,
        })))
      }).catch(() => {});
      return nuevosLeads;
    });
  };

  // Switch general: pausa la búsqueda y el envío automático (el dashboard sigue andando).
  // OJO Sebas: hoy esto solo guarda el flag en schedule.json. Para que realmente frene
  // los agentes hace falta que agente_lotes_<empresa>.py y hermes.py chequeen
  // schedule.sistema_activo al arrancar y corten si está en false — pendiente del lado server.
  const toggleSistema = () => {
    const nuevoEstado = !schedule.sistema_activo;
    if (!window.confirm(nuevoEstado
      ? "¿Reactivar la búsqueda y el envío automático de emails?"
      : "¿Pausar la búsqueda y el envío automático de emails? El dashboard sigue funcionando normalmente."
    )) return;
    setSchedule(prev => {
      const nuevo = { ...prev, sistema_activo: nuevoEstado };
      fetch(`/api/schedule/${EMPRESA_SLUG}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(nuevo),
      }).catch(() => {});
      return nuevo;
    });
  };

  // Segmentos únicos
  const segmentos = ["Todos", ...Array.from(new Set(leads.map(l=>l.segmento).filter(Boolean)))];

  if (!usuario) return <Login vendedores={vendedores} onLogin={setUsuario}/>;

  const tabs = [
    { id:"dashboard",  label:"📊 Dashboard" },
    { id:"kanban",     label:"🗂 Pipeline" },
    { id:"leads",      label:"📋 Leads" },
    { id:"actividad",  label:"⚡ Actividad" },
    { id:"emails",     label:"✉️ Emails" },
    ...(esAdmin ? [
      { id:"config",  label:"⚙️ Configuración" },
      { id:"admin",   label:"📊 Admin" },
    ] : []),
  ];

  return (
    <div style={{ minHeight:"100vh", background:"var(--brand-paper)", fontFamily:"'Georgia','Segoe UI',sans-serif", color:"var(--brand-ink)" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;800;900&display=swap');
        *{box-sizing:border-box} ::-webkit-scrollbar{width:4px;height:4px}
        ::-webkit-scrollbar-thumb{background:rgba(var(--brand-ink-rgb),0.15);border-radius:2px}
        select option{background:var(--brand-paper)}
        @media(max-width:768px){
          .kanban-grid{flex-direction:column!important}
          .kanban-col{width:100%!important;min-width:unset!important}
          .dash-kpis{grid-template-columns:1fr 1fr!important}
          .dash-charts{grid-template-columns:1fr!important}
          .config-grid{grid-template-columns:1fr!important}
          .modal-inner{width:100%!important;max-width:100%!important;height:100%!important;max-height:100%!important;border-radius:0!important;overflow-y:auto}
          .modal-overlay{align-items:flex-end!important;padding:0!important}
          .tabs-bar{overflow-x:auto;-webkit-overflow-scrolling:touch;scrollbar-width:none}
          .tabs-bar::-webkit-scrollbar{display:none}
        }
      `}</style>

      {/* HEADER */}
      <div style={{
        height: isMobile ? 52 : 56,
        padding: isMobile ? "0 16px" : "0 24px",
        borderBottom:"1px solid rgba(var(--brand-ink-rgb),0.1)",
        display:"flex", alignItems:"center", justifyContent:"space-between",
        background:"var(--brand-paper)", position:"sticky", top:0, zIndex:50,
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontFamily:"Georgia,serif", fontSize: isMobile ? 16 : 18, fontWeight:"normal", color:"var(--brand-ink)", letterSpacing:"0.06em" }}>{branding.logoUrl ? <img src={branding.logoUrl} alt={branding.nombre} style={{ height: isMobile?20:24, verticalAlign:"middle" }}/> : branding.nombre}</span>
          {!isMobile && <span style={{ fontSize:10, letterSpacing:"0.18em", textTransform:"uppercase", color:"#888884" }}>CRM</span>}
          {nuevosHoy > 0 && (
            <span style={{ fontSize:10, fontWeight:700, background:"rgba(var(--brand-ink-rgb),0.08)", color:"#444440", padding:"2px 8px", borderRadius:20 }}>
              +{nuevosHoy} hoy
            </span>
          )}
        </div>

        <div className="tabs-bar" style={{ display:"flex", gap:2, maxWidth: isMobile ? "55vw" : "unset" }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: isMobile ? "5px 10px" : "5px 14px",
              borderRadius:7, border:"none",
              background: tab===t.id ? "rgba(var(--brand-ink-rgb),0.08)" : "transparent",
              color: tab===t.id ? "var(--brand-ink)" : "#888884",
              fontWeight: tab===t.id ? 700 : 500,
              fontSize: isMobile ? 11 : 12,
              cursor:"pointer", whiteSpace:"nowrap",
            }}>{t.label}</button>
          ))}
        </div>

        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          {esAdmin && (
            <button onClick={toggleSistema} title="Pausa/reactiva la búsqueda y el envío automático" style={{
              display:"flex", alignItems:"center", gap:6,
              padding:"4px 10px", borderRadius:20, border:"1px solid rgba(var(--brand-ink-rgb),0.12)",
              background: schedule.sistema_activo ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)",
              color: schedule.sistema_activo ? "#10B981" : "#EF4444",
              cursor:"pointer", fontSize:11, fontWeight:700,
            }}>
              <span style={{
                width:7, height:7, borderRadius:"50%",
                background: schedule.sistema_activo ? "#10B981" : "#EF4444",
              }}/>
              {isMobile ? "" : (schedule.sistema_activo ? "Sistema activo" : "Sistema pausado")}
            </button>
          )}
          {!isMobile && <span style={{ fontSize:11, fontFamily:"Georgia,serif", fontStyle:"italic", color:"#888884" }}>{usuario.nombre}</span>}
          <button onClick={() => setUsuario(null)} style={{
            padding:"4px 10px", borderRadius:6, border:"1px solid rgba(var(--brand-ink-rgb),0.12)",
            background:"transparent", color:"#888884", cursor:"pointer", fontSize:11,
          }}>{isMobile ? "↩" : "Salir"}</button>
        </div>
      </div>

      <div style={{ padding: isMobile ? "12px 10px" : "20px 16px", maxWidth:1400, margin:"0 auto" }}>

        {/* ── LEADS ── */}
        {tab === "leads" && (
          <TabLeads leads={leads}/>
        )}

        {/* ── ACTIVIDAD ── */}
        {tab === "actividad" && (
          <TabActividad leads={leads} esAdmin={esAdmin} />
        )}

        {/* ── EMAILS ── */}
        {tab === "emails" && (
          <TabEmails esAdmin={esAdmin} schedule={schedule} setSchedule={setSchedule} leads={leads} pin={usuario?.pin} />
        )}

        {/* ── KANBAN ── */}
        {tab === "kanban" && (
          <>
            {/* Filtros */}
            <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
              <input
                value={busqueda}
                onChange={e=>setBusqueda(e.target.value)}
                placeholder="🔍 Buscar empresa..."
                style={{
                  flex:1, minWidth:160, background:"rgba(255,255,255,0.05)",
                  border:"1px solid rgba(255,255,255,0.08)", borderRadius:8,
                  padding:"8px 12px", color:"var(--brand-ink)", fontSize:12, outline:"none",
                }}
              />
              {[
                { val:filtroEmpresa,   set:setFiltroEmpresa,   opts:EMPRESAS_CAPTIO,          label:"Empresa" },
                { val:filtroRegion,    set:setFiltroRegion,    opts:["Todas",...regiones,"Otros"], label:"Región" },
                { val:filtroSegmento,  set:setFiltroSegmento,  opts:segmentos,                label:"Rubro"  },
                ...(esAdmin ? [{ val:filtroVendedor, set:setFiltroVendedor, opts:["Todos",...vendedores.filter(v=>v.rol==="vendedor").map(v=>v.id)], label:"Vendedor" }] : []),
              ].map(f => (
                <select key={f.label} value={f.val} onChange={e=>f.set(e.target.value)} style={{
                  background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)",
                  borderRadius:8, padding:"8px 12px", color:"var(--brand-ink)", fontSize:12, outline:"none",
                }}>
                  {f.opts.map(o => (
                    <option key={o} value={o}>
                      {f.label==="Vendedor" ? (vendedores.find(v=>v.id===o)?.nombre || "Todos") : o}
                    </option>
                  ))}
                </select>
              ))}
            </div>

            {/* Columnas kanban */}
            <div className="kanban-grid" style={{
              display:"flex", gap:10, alignItems:"flex-start",
              overflowX: "auto", WebkitOverflowScrolling:"touch",
              paddingBottom:8,
            }}>
              {COLUMNAS.map(col => (
                <Columna
                  key={col.id}
                  col={col}
                  leads={leadsFiltrados.filter(l=>l.estado===col.id)}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onTap={lead => setModal({ tipo:"detalle", lead })}
                  esAdmin={esAdmin}
                  vendedores={vendedores}
                />
              ))}
            </div>
          </>
        )}

        {/* ── DASHBOARD NASA ── */}
        {tab === "dashboard" && (
          <div>
            {/* Fila 1 — KPIs grandes */}
            <div className="dash-kpis" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:10, marginBottom:20 }}>
              {[
                { icon:"🆕", label:"Leads totales",    value:leads.length,          color:"#8B5CF6", sub:"en el sistema" },
                { icon:"✅", label:"Validados",         value:leadsValidados,        color:"#3B82F6", sub:"con interés confirmado" },
                { icon:"💬", label:"Cotizados",         value:leads.filter(l=>l.estado==="Cotizado").length, color:"#F59E0B", sub:"esperando respuesta" },
                { icon:"🏆", label:"Ganados",           value:leads.filter(l=>l.estado==="Ganado").length,  color:"#10B981", sub:"cierres exitosos" },
                { icon:"💰", label:"Pipeline $",        value:fmt$(pipeline),        color:"#F59E0B", sub:"en cotizaciones" },
                { icon:"📈", label:"Closing rate",      value:`${tasaCierre}%`,      color:"var(--brand-ink)", sub:"tasa de cierre" },
                { icon:"⭐", label:"Valor promedio",    value:fmt$(valorPromedio),   color:"#10B981", sub:"por operación ganada" },
                { icon:"🔥", label:"Nuevos hoy",        value:nuevosHoy,             color:"#EF4444", sub:"leads del día" },
              ].map(k => (
                <div key={k.label} style={{
                  background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)",
                  borderRadius:12, padding:"14px 16px", position:"relative", overflow:"hidden",
                }}>
                  <div style={{ position:"absolute", top:8, right:10, fontSize:20, opacity:0.15 }}>{k.icon}</div>
                  <div style={{ fontSize:24, fontWeight:900, color:k.color, lineHeight:1 }}>{k.value}</div>
                  <div style={{ fontSize:11, fontWeight:700, color:"rgba(var(--brand-ink-rgb),0.7)", marginTop:6 }}>{k.label}</div>
                  <div style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.3)", marginTop:2 }}>{k.sub}</div>
                </div>
              ))}
            </div>

            {/* Fila 2 — Gráficos */}
            <div className="dash-charts" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>

              {/* Pipeline por empresa */}
              <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:18 }}>
                <div style={{ fontWeight:700, fontSize:13, marginBottom:4, color:"var(--brand-ink)" }}>Pipeline por empresa</div>
                <div style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.3)", marginBottom:14 }}>Leads activos por proyecto</div>
                {[branding.nombre].map((emp, ei) => {
                  const cols = ["var(--brand-ink)","#F59E0B","#06B6D4"];
                  const eLeads = leads.filter(l=>l.empresa_captio===emp);
                  const ePct = leads.length ? Math.round(eLeads.length/leads.length*100) : 0;
                  const eMonto = eLeads.filter(l=>l.monto>0).reduce((a,b)=>a+b.monto,0);
                  return (
                    <div key={emp} style={{ marginBottom:14 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                        <span style={{ fontSize:12, color:"rgba(var(--brand-ink-rgb),0.7)", fontWeight:500 }}>{emp}</span>
                        <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                          {eMonto>0 && <span style={{ fontSize:10, color:cols[ei], fontWeight:700 }}>{fmt$(eMonto)}</span>}
                          <span style={{ fontSize:11, fontWeight:700, color:cols[ei] }}>{eLeads.length} leads</span>
                        </div>
                      </div>
                      <div style={{ height:6, background:"rgba(255,255,255,0.06)", borderRadius:3 }}>
                        <div style={{ height:"100%", width:`${Math.max(ePct,2)}%`, background:cols[ei], borderRadius:3, transition:"width 0.5s" }}/>
                      </div>
                      <div style={{ display:"flex", gap:6, marginTop:5 }}>
                        {["Nuevo","Validado","Cotizado","Ganado"].map(est => {
                          const n = eLeads.filter(l=>l.estado===est).length;
                          if (!n) return null;
                          const estCol = {Nuevo:"#6B7280",Validado:"#3B82F6",Cotizado:"#F59E0B",Ganado:"#10B981"}[est];
                          return <span key={est} style={{ fontSize:9, color:estCol, background:`${estCol}20`, padding:"1px 6px", borderRadius:10 }}>{est}: {n}</span>;
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Leads por ciudad */}
              <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:18 }}>
                <div style={{ fontWeight:700, fontSize:13, marginBottom:4, color:"var(--brand-ink)" }}>Leads por ciudad</div>
                <div style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.3)", marginBottom:14 }}>Distribución geográfica</div>
                {(() => {
                  const ciudades = ["CABA","Córdoba","Rosario","Mendoza","Santa Fe"];
                  const cols = ["#8B5CF6","var(--brand-ink)","#06B6D4","#10B981","#F59E0B"];
                  const otrosLeads = leads.filter(l=>!ciudades.some(c=>l.region===c||l.ciudad?.includes(c)));
                  const items = [
                    ...ciudades.map((ciudad,ci) => ({
                      label: ciudad,
                      n: leads.filter(l=>l.region===ciudad||l.ciudad?.includes(ciudad)).length,
                      color: cols[ci],
                    })),
                    { label:"Otros", n: otrosLeads.length, color:"#4B5563" },
                  ].filter(x=>x.n>0);
                  const max = Math.max(...items.map(x=>x.n), 1);
                  return items.map(({ label, n, color }) => (
                    <div key={label} style={{ marginBottom:10 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                        <span style={{ fontSize:12, color:"rgba(var(--brand-ink-rgb),0.65)" }}>{label}</span>
                        <span style={{ fontSize:12, fontWeight:700, color }}>{n}</span>
                      </div>
                      <div style={{ height:6, background:"rgba(255,255,255,0.06)", borderRadius:3 }}>
                        <div style={{ height:"100%", width:`${Math.round(n/max*100)}%`, background:color, borderRadius:3, transition:"width 0.5s" }}/>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* Ranking vendedores eliminado — clientes nuevos arrancan con un único vendedor admin */}

            {/* FILA 4 — Leads calientes + Actividad reciente */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginTop:14, marginBottom:14 }}>

              {/* Leads calientes */}
              <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:18 }}>
                <div style={{ fontWeight:700, fontSize:13, color:"var(--brand-ink)", marginBottom:4 }}>🔥 Leads calientes</div>
                <div style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.35)", marginBottom:14 }}>Mejores oportunidades sin contactar · hacé clic para actuar</div>
                {[...leads]
                  .filter(l=>["Nuevo","Validado"].includes(l.estado))
                  .sort((a,b)=>b.score-a.score)
                  .slice(0,7)
                  .map(l => (
                    <div key={l.id} style={{
                      display:"flex", alignItems:"center", gap:10, padding:"9px 12px",
                      background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)",
                      borderRadius:8, marginBottom:6, cursor:"pointer",
                    }} onClick={() => setModal({ tipo:"detalle", lead:l })}>
                      <div style={{
                        width:36, height:36, borderRadius:8, flexShrink:0,
                        background: l.score>=85?"rgba(16,185,129,0.12)":l.score>=70?"rgba(245,158,11,0.12)":"rgba(107,114,128,0.1)",
                        display:"flex", alignItems:"center", justifyContent:"center",
                        fontSize:13, fontWeight:900,
                        color: l.score>=85?"#10B981":l.score>=70?"#F59E0B":"#6B7280",
                      }}>{l.score}</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:"var(--brand-ink)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{l.empresa}</div>
                        <div style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.4)" }}>{l.cargo||l.segmento} · {l.region}</div>
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4 }}>
                        <span style={{ fontSize:9, padding:"2px 7px", borderRadius:10, background: l.estado==="Validado"?"rgba(59,130,246,0.1)":"rgba(107,114,128,0.1)", color: l.estado==="Validado"?"#3B82F6":"#6B7280" }}>{l.estado}</span>
                        {l.nombre_linkedin && <span style={{ fontSize:9, color:"#0077B5" }}>👤 {l.nombre_linkedin.split(" ")[0]}</span>}
                      </div>
                    </div>
                  ))}
                {leads.filter(l=>["Nuevo","Validado"].includes(l.estado)).length===0 && (
                  <div style={{ fontSize:12, color:"rgba(var(--brand-ink-rgb),0.3)", textAlign:"center", padding:"20px 0" }}>No hay leads pendientes de contactar</div>
                )}
              </div>

              {/* Actividad reciente */}
              <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:18 }}>
                <div style={{ fontWeight:700, fontSize:13, color:"var(--brand-ink)", marginBottom:4 }}>📨 Actividad reciente</div>
                <div style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.35)", marginBottom:14 }}>Últimas acciones del sistema</div>
                {(() => {
                  const recientes = [...leads]
                    .filter(l=>l.estado_email||l.fecha)
                    .sort((a,b)=>new Date(b.fecha||0)-new Date(a.fecha||0))
                    .slice(0,8);
                  if(recientes.length===0) return (
                    <div style={{ fontSize:12, color:"rgba(var(--brand-ink-rgb),0.3)", textAlign:"center", padding:"20px 0" }}>Sin actividad registrada</div>
                  );
                  return recientes.map(l => {
                    const tipoEmail = l.estado_email?.replace("_"," ").replace("enviado","") || "";
                    const icon = l.estado==="Ganado"?"🏆":l.estado==="Cotizado"?"📄":l.estado_email?"✉️":"🆕";
                    return (
                      <div key={l.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:"1px solid rgba(var(--brand-ink-rgb),0.05)", cursor:"pointer" }}
                        onClick={() => setModal({ tipo:"detalle", lead:l })}>
                        <div style={{ fontSize:16, flexShrink:0 }}>{icon}</div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:12, fontWeight:600, color:"var(--brand-ink)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{l.empresa}</div>
                          <div style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.35)" }}>
                            {l.estado_email ? `Email ${tipoEmail}` : `Lead ${l.estado}`} · {l.region}
                          </div>
                        </div>
                        <div style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.3)", flexShrink:0 }}>{l.fecha?.slice(5)||""}</div>
                      </div>
                    );
                  });
                })()}
              </div>

            </div>

          </div>
        )}

        {/* ── CONFIGURACIÓN — 5 secciones ── */}
        {tab === "config" && esAdmin && (() => {
          const cfg = configEditando || configs[configEmpresa] || {};
          const empColor = "var(--brand-ink)";

          const guardarConfig = () => {
            fetch(`/api/config/${configEmpresa}`, {
              method:"POST", headers:{"Content-Type":"application/json"},
              body: JSON.stringify(cfg, null, 2),
            }).then(() => {
              setConfigs(prev => ({ ...prev, [configEmpresa]: cfg }));
              setConfigEditando(null);
              alert("✅ Configuración guardada");
            }).catch(() => alert("❌ Error al guardar"));
          };

          const DIAS = ["D","L","M","X","J","V","S"];
          const DIAS_POR_PLAN = { starter: 2, business: 3, pro: 5 };
          const MAX_DIAS_PLAN = DIAS_POR_PLAN[(cfg.plan || "").toLowerCase()] || 2;

          const toggleDia = (key, idx) => {
            const arr = [...(schedule[key] || [])];
            const pos = arr.indexOf(idx);
            if (pos >= 0) {
              arr.splice(pos, 1);
            } else {
              if (arr.length >= MAX_DIAS_PLAN) {
                alert(`Tu plan permite hasta ${MAX_DIAS_PLAN} días para esta acción. Para más volumen, hay que pasar a un plan superior.`);
                return;
              }
              arr.push(idx);
            }
            setSchedule(prev => ({ ...prev, [key]: arr.sort() }));
          };

          const guardarSchedule = () => {
            fetch(`/api/schedule/${configEmpresa}`, {
              method:"POST", headers:{"Content-Type":"application/json"},
              body: JSON.stringify(schedule),
            }).then(() => alert("✅ Horario guardado"))
              .catch(() => alert("❌ Error al guardar horario"));
          };

          const MAX_VENDEDORES_PLAN = 3; // 1 admin + 2 extra — más usuarios = upgrade

          const generarPin = () => String(Math.floor(1000 + Math.random()*9000));

          const persistirVendedores = (lista) => {
            fetch(`/api/vendedores/${EMPRESA_SLUG}`, {
              method:"POST", headers:{"Content-Type":"application/json"},
              body: JSON.stringify(lista),
            }).catch(() => alert("❌ Error al guardar usuarios"));
          };

          const agregarVendedor = () => {
            const nombre = nuevoVendedorNombre.trim();
            if (!nombre) return;
            if (vendedores.length >= MAX_VENDEDORES_PLAN) return;
            const nuevo = {
              id: `v${Date.now()}`,
              nombre,
              pin: generarPin(),
              rol: "vendedor",
            };
            const lista = [...vendedores, nuevo];
            setVendedores(lista);
            persistirVendedores(lista);
            setNuevoVendedorNombre("");
          };

          const eliminarVendedor = (id) => {
            if (!window.confirm("¿Eliminar este usuario? No va a poder volver a entrar.")) return;
            const lista = vendedores.filter(v => v.id !== id);
            setVendedores(lista);
            persistirVendedores(lista);
          };

          const PROVEEDORES_SMTP = [
            { label:"Gmail",         host:"smtp.gmail.com",      port:"465", modo:"ssl"      },
            { label:"IONOS",         host:"smtp.ionos.es",       port:"587", modo:"starttls" },
            { label:"Outlook",       host:"smtp.office365.com",  port:"587", modo:"starttls" },
            { label:"Yahoo",         host:"smtp.mail.yahoo.com", port:"465", modo:"ssl"      },
            { label:"Hostinger",     host:"smtp.hostinger.com",  port:"465", modo:"ssl"      },
            { label:"OVH",           host:"ssl0.ovh.net",        port:"465", modo:"ssl"      },
            { label:"Otro / manual", host:"",                    port:"587", modo:"starttls" },
          ];

          const smtpAutoDetectado = (() => {
            if (!smtpConfig.email || !smtpConfig.email.includes("@")) return null;
            const dom = smtpConfig.email.split("@")[1].toLowerCase();
            if (dom === "gmail.com")                               return PROVEEDORES_SMTP[0];
            if (dom.includes("outlook")||dom.includes("hotmail"))  return PROVEEDORES_SMTP[2];
            if (dom === "yahoo.com")                               return PROVEEDORES_SMTP[3];
            return null;
          })();

          const smtpDetectado = smtpAutoDetectado || PROVEEDORES_SMTP.find(p => p.host === smtpConfig.host) || null;

          const guardarSmtp = () => {
            setSmtpStatus("saving");
            const payload = { ...smtpConfig, ...(smtpDetectado||{}) };
            fetch(`/api/smtp/${configEmpresa}`, {
              method:"POST", headers:{"Content-Type":"application/json"},
              body: JSON.stringify(payload),
            }).then(r => r.json())
              .then(d => setSmtpStatus(d.ok ? "ok" : "error"))
              .catch(() => setSmtpStatus("error"));
          };

          const probarSmtp = () => {
            setSmtpStatus("testing");
            const payload = { ...smtpConfig, ...(smtpDetectado||{}) };
            fetch(`/api/smtp/${configEmpresa}/test`, {
              method:"POST", headers:{"Content-Type":"application/json"},
              body: JSON.stringify(payload),
            }).then(r => r.json())
              .then(d => setSmtpStatus(d.ok ? "ok" : "error"))
              .catch(() => setSmtpStatus("error"));
          };

          const sCard = {
            background:"#fff", border:"1px solid rgba(var(--brand-ink-rgb),0.1)",
            borderRadius:14, padding:20, marginBottom:16,
          };
          const sLabel = { fontSize:11, color:"rgba(var(--brand-ink-rgb),0.4)", marginBottom:5, display:"block" };
          const sInput = {
            width:"100%", border:"1px solid rgba(var(--brand-ink-rgb),0.12)", borderRadius:8,
            padding:"8px 12px", fontSize:13, color:"var(--brand-ink)", outline:"none",
            background:"rgba(var(--brand-ink-rgb),0.02)", boxSizing:"border-box",
          };
          const sBtn = (primary) => ({
            padding:"9px 18px", borderRadius:8, border: primary ? "none" : "1px solid rgba(var(--brand-ink-rgb),0.2)",
            background: primary ? "var(--brand-ink)" : "transparent",
            color: primary ? "var(--brand-paper)" : "var(--brand-ink)",
            fontWeight:600, fontSize:13, cursor:"pointer",
          });

          const PAISES_DISPONIBLES = ["España","Francia","Italia","Alemania","Portugal","Reino Unido","México","Argentina","Colombia","Chile","China","EEUU"];

          const paisesActivos = cfg.paises || ["España"];

          const togglePais = (p) => {
            const arr = paisesActivos.includes(p) ? paisesActivos.filter(x=>x!==p) : [...paisesActivos, p];
            setConfigEditando({...cfg, paises: arr});
          };

          return (
            <div style={{ maxWidth:"100%" }}>
              <div style={{ marginBottom:20 }}>
                <div style={{ fontWeight:800, fontSize:18, color:"var(--brand-ink)", marginBottom:4 }}>⚙️ Configuración</div>
                <div style={{ fontSize:12, color:"rgba(var(--brand-ink-rgb),0.35)" }}>Todo lo que el agente necesita para trabajar de forma autónoma</div>
              </div>

              {/* ── SEMÁFORO ESTADO DEL SISTEMA ── */}
              <div style={{ background:"rgba(var(--brand-ink-rgb),0.03)", border:"1px solid rgba(var(--brand-ink-rgb),0.08)", borderRadius:10, padding:"10px 14px", marginBottom:16 }}>
                <div style={{ fontWeight:700, fontSize:11, color:"rgba(var(--brand-ink-rgb),0.5)", marginBottom:8, textTransform:"uppercase", letterSpacing:0.5 }}>Estado del sistema</div>
                {estadoSistema ? (
                  <>
                    {["envios","respuestas","busqueda","calificacion"].map(motor => {
                      const info = estadoSistema[motor];
                      if (!info) return (
                        <div key={motor} style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                          <span style={{ width:8, height:8, borderRadius:"50%", background:"#D1D5DB", display:"inline-block" }} />
                          <span style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.4)" }}>{motor}: sin datos</span>
                        </div>
                      );
                      const horas = (Date.now() - new Date(info.fecha).getTime()) / 3600000;
                      const color = !info.ok ? "#EF4444" : horas > 48 ? "#F59E0B" : "#10B981";
                      const label = !info.ok ? "error" : horas > 48 ? "inactivo" : "ok";
                      return (
                        <div key={motor} style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                          <span style={{ width:8, height:8, borderRadius:"50%", background:color, display:"inline-block", boxShadow:`0 0 4px ${color}` }} />
                          <span style={{ fontSize:11, color:"var(--brand-ink)" }}>{motor}</span>
                          <span style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.4)", marginLeft:"auto" }}>{label}</span>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <div style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.35)" }}>Cargando...</div>
                )}
              </div>

              {/* ── FILA 1: SEGMENTOS + EMAIL ── */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>

                {/* SECCIÓN 1: SEGMENTOS */}
                <div style={{...sCard, marginBottom:0}}>
                  <div style={{ fontWeight:700, fontSize:14, color:"var(--brand-ink)", marginBottom:4 }}>1. Segmentos de búsqueda</div>
                  <div style={{ fontSize:12, color:"rgba(var(--brand-ink-rgb),0.4)", marginBottom:14 }}>Dónde y a quién busca el agente.</div>

                  {/* Países */}
                  <label style={sLabel}>PAÍSES</label>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:14 }}>
                    {PAISES_DISPONIBLES.map(p => (
                      <button key={p} onClick={() => togglePais(p)} style={{
                        padding:"4px 12px", borderRadius:20, fontSize:12, cursor:"pointer", fontWeight:500,
                        border: paisesActivos.includes(p) ? "none" : "1px solid rgba(var(--brand-ink-rgb),0.15)",
                        background: paisesActivos.includes(p) ? "var(--brand-ink)" : "transparent",
                        color: paisesActivos.includes(p) ? "var(--brand-paper)" : "rgba(var(--brand-ink-rgb),0.45)",
                      }}>{p}</button>
                    ))}
                  </div>

                  {/* Sectores */}
                  <label style={sLabel}>SECTORES — el agente rota entre ellos</label>
                  {(cfg.segmentos||[]).map((seg, si) => (
                    <div key={si} style={{ display:"flex", gap:6, marginBottom:6 }}>
                      <input value={seg} onChange={e => { const s=[...(cfg.segmentos||[])]; s[si]=e.target.value; setConfigEditando({...cfg,segmentos:s}); }} style={{...sInput, flex:1}} />
                      <button onClick={() => { const s=(cfg.segmentos||[]).filter((_,i)=>i!==si); setConfigEditando({...cfg,segmentos:s}); }} style={{ background:"none", border:"none", color:"#EF4444", cursor:"pointer", fontSize:18, padding:"0 4px" }}>×</button>
                    </div>
                  ))}
                  <button onClick={() => setConfigEditando({...cfg, segmentos:[...(cfg.segmentos||[]),""] })}
                    style={{ marginTop:2, marginBottom:14, padding:"4px 12px", borderRadius:6, border:"1px dashed rgba(var(--brand-ink-rgb),0.25)", background:"none", color:"rgba(var(--brand-ink-rgb),0.5)", cursor:"pointer", fontSize:12 }}>+ Agregar sector</button>

                  {/* Ciudades */}
                  <label style={sLabel}>CIUDADES — el agente rota entre ellas</label>
                  {(cfg.regiones||[]).map((reg, ri) => (
                    <div key={ri} style={{ display:"flex", gap:6, marginBottom:6 }}>
                      <input value={reg} onChange={e => { const r=[...(cfg.regiones||[])]; r[ri]=e.target.value; setConfigEditando({...cfg,regiones:r}); }} style={{...sInput, flex:1}} />
                      <button onClick={() => { const r=(cfg.regiones||[]).filter((_,i)=>i!==ri); setConfigEditando({...cfg,regiones:r}); }} style={{ background:"none", border:"none", color:"#EF4444", cursor:"pointer", fontSize:18, padding:"0 4px" }}>×</button>
                    </div>
                  ))}
                  <button onClick={() => setConfigEditando({...cfg, regiones:[...(cfg.regiones||[]),""] })}
                    style={{ marginTop:2, marginBottom:14, padding:"4px 12px", borderRadius:6, border:"1px dashed rgba(var(--brand-ink-rgb),0.25)", background:"none", color:"rgba(var(--brand-ink-rgb),0.5)", cursor:"pointer", fontSize:12 }}>+ Agregar ciudad</button>

                  {/* Score + Decisor */}
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
                    <div>
                      <label style={sLabel}>Score mínimo</label>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <input type="range" min="0" max="100" value={cfg.score_minimo||65}
                          onChange={e => setConfigEditando({...cfg, score_minimo:parseInt(e.target.value)})} style={{ flex:1 }} />
                        <span style={{ fontWeight:700, fontSize:15, color:"var(--brand-ink)", minWidth:28 }}>{cfg.score_minimo||65}</span>
                      </div>
                    </div>
                    <div>
                      <label style={sLabel}>Decisor objetivo</label>
                      <input value={cfg.decisor||""} onChange={e => setConfigEditando({...cfg,decisor:e.target.value})} style={sInput} />
                    </div>
                  </div>

                  <div style={{ marginBottom:12 }}>
                    <label style={sLabel}>Descripción del servicio</label>
                    <textarea value={cfg.servicio||""} onChange={e => setConfigEditando({...cfg,servicio:e.target.value})}
                      style={{...sInput, minHeight:52, resize:"vertical"}} />
                  </div>

                  {configEditando && (
                    <div style={{ display:"flex", gap:8 }}>
                      <button onClick={guardarConfig} style={sBtn(true)}>💾 Guardar</button>
                      <button onClick={() => setConfigEditando(null)} style={sBtn(false)}>Cancelar</button>
                    </div>
                  )}
                </div>

                {/* SECCIÓN 2: DÍAS DE BÚSQUEDA */}
                <div style={{...sCard, marginBottom:0}}>
                  <div style={{ fontWeight:700, fontSize:14, color:"var(--brand-ink)", marginBottom:4 }}>2. Días de búsqueda</div>
                  <div style={{ fontSize:12, color:"rgba(var(--brand-ink-rgb),0.4)", marginBottom:4 }}>El agente buscará nuevas empresas estos días.</div>
                  <div style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.35)", marginBottom:12, fontStyle:"italic" }}>Plan base: hasta {MAX_DIAS_PLAN} días. Más volumen requiere upgrade.</div>
                  <div style={{ display:"flex", gap:6, marginBottom:14 }}>
                    {DIAS.map((d,i) => {
                      const activo = (schedule.dias_busqueda||[]).includes(i);
                      const bloqueado = !activo && (schedule.dias_busqueda||[]).length >= MAX_DIAS_PLAN;
                      return (
                      <button key={i} onClick={() => toggleDia("dias_busqueda", i)} style={{
                        width:36, height:36, borderRadius:"50%", border:"1px solid rgba(var(--brand-ink-rgb),0.15)",
                        cursor: bloqueado ? "not-allowed" : "pointer", fontSize:12, fontWeight:600,
                        background: activo ? "var(--brand-ink)" : "transparent",
                        color: activo ? "var(--brand-paper)" : bloqueado ? "rgba(var(--brand-ink-rgb),0.2)" : "rgba(var(--brand-ink-rgb),0.5)",
                        opacity: bloqueado ? 0.5 : 1,
                      }}>{d}</button>
                    );})}
                  </div>
                  <button onClick={guardarSchedule} style={sBtn(true)}>💾 Guardar días</button>

                  <div style={{ marginTop:18, paddingTop:16, borderTop:"1px solid rgba(var(--brand-ink-rgb),0.08)" }}>
                    <div style={{ fontSize:12, color:"rgba(var(--brand-ink-rgb),0.45)", marginBottom:10 }}>¿Necesitás más días de búsqueda? Solicitá el upgrade:</div>
                    <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                      {[
                        { id:"starter",  nombre:"Starter",  dias:2 },
                        { id:"business", nombre:"Business", dias:3 },
                        { id:"pro",      nombre:"Pro",      dias:5 },
                      ].map(p => {
                        const esActual = (cfg.plan || "starter").toLowerCase() === p.id;
                        return (
                          <button key={p.id} disabled={esActual}
                            onClick={() => {
                              const asunto = encodeURIComponent(`Solicitud de upgrade — ${branding.nombre} (${EMPRESA_SLUG})`);
                              const cuerpo = encodeURIComponent(`Hola,\n\nQuiero pasar al plan ${p.nombre} (${p.dias} días de búsqueda por semana) para la cuenta ${branding.nombre} (${EMPRESA_SLUG}).\n\nGracias.`);
                              window.open(`mailto:hola@velqo.io?subject=${asunto}&body=${cuerpo}`, "_blank");
                            }}
                            style={{
                              padding:"8px 16px", borderRadius:20,
                              border: esActual ? "1.5px solid var(--brand-ink)" : "1px solid rgba(var(--brand-ink-rgb),0.2)",
                              background: esActual ? "var(--brand-ink)" : "transparent",
                              color: esActual ? "var(--brand-paper)" : "var(--brand-ink)",
                              fontSize:12, fontWeight:600, cursor: esActual ? "default" : "pointer",
                              opacity: esActual ? 1 : 0.85,
                            }}>
                            {p.nombre} · {p.dias} días{esActual ? " (actual)" : ""}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

              </div>

              {/* ── SECCIÓN 3: EMAIL SALIENTE (ancho completo) ── */}
              <div style={sCard}>
                <div style={{ fontWeight:700, fontSize:14, color:"var(--brand-ink)", marginBottom:4 }}>3. Email de salida (SMTP)</div>
                <div style={{ fontSize:12, color:"rgba(var(--brand-ink-rgb),0.4)", marginBottom:14 }}>Desde dónde salen los emails. SMTP detectado automáticamente.</div>

                {/* Selector de proveedor */}
                <div style={{ marginBottom:12 }}>
                  <label style={sLabel}>PROVEEDOR</label>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                    {PROVEEDORES_SMTP.map(p => (
                      <button key={p.label} onClick={() => setSmtpConfig(prev => ({...prev, host:p.host, port:p.port, modo:p.modo}))}
                        style={{ padding:"4px 12px", borderRadius:20, fontSize:12, cursor:"pointer", fontWeight:500,
                          border: (smtpConfig.host===p.host||(p.label==="Otro / manual"&&!PROVEEDORES_SMTP.slice(0,-1).find(x=>x.host===smtpConfig.host))) ? "none" : "1px solid rgba(var(--brand-ink-rgb),0.15)",
                          background: (smtpConfig.host===p.host||(p.label==="Otro / manual"&&!PROVEEDORES_SMTP.slice(0,-1).find(x=>x.host===smtpConfig.host))) ? "var(--brand-ink)" : "transparent",
                          color: (smtpConfig.host===p.host||(p.label==="Otro / manual"&&!PROVEEDORES_SMTP.slice(0,-1).find(x=>x.host===smtpConfig.host))) ? "var(--brand-paper)" : "rgba(var(--brand-ink-rgb),0.45)",
                        }}>{p.label}</button>
                    ))}
                  </div>
                </div>

                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:12 }}>
                  <div>
                    <label style={sLabel}>Email remitente</label>
                    <input type="email" value={smtpConfig.email||""} placeholder="tucorreo@tudominio.com"
                      onChange={e => { setSmtpConfig(p=>({...p,email:e.target.value})); setSmtpStatus(null); }} style={sInput} />
                  </div>
                  <div>
                    <label style={sLabel}>Contraseña</label>
                    <input type="password" value={smtpConfig.password||""} placeholder="••••••••••••"
                      onChange={e => { setSmtpConfig(p=>({...p,password:e.target.value})); setSmtpStatus(null); }} style={sInput} />
                    {smtpConfig.host==="smtp.gmail.com" && (
                      <div style={{ marginTop:6, padding:"8px 10px", borderRadius:7, background:"rgba(234,179,8,0.08)", border:"1px solid rgba(234,179,8,0.25)", fontSize:11, color:"#92400e", lineHeight:1.5 }}>
                        ⚠️ <strong>Gmail requiere una contraseña de aplicación</strong>, no tu contraseña normal.<br/>
                        1. Activá la verificación en dos pasos en tu cuenta Google<br/>
                        2. Andá a <a href="https://myaccount.google.com/apppasswords" target="_blank" style={{ color:"#92400e" }}>myaccount.google.com/apppasswords</a><br/>
                        3. Creá una clave para "Captio" y pegala acá (16 caracteres sin espacios)
                      </div>
                    )}
                    {smtpConfig.host==="smtp.ionos.es" && (
                      <div style={{ marginTop:6, padding:"8px 10px", borderRadius:7, background:"rgba(59,130,246,0.06)", border:"1px solid rgba(59,130,246,0.2)", fontSize:11, color:"#1e40af", lineHeight:1.5 }}>
                        ℹ️ Usá la contraseña normal de tu cuenta IONOS.<br/>
                        Si el envío falla, IONOS puede haber bloqueado la IP del servidor.<br/>
                        Autorizala en: <a href="https://www.ionos.com/help/index.php?id=2425&ip=217.160.163.249" target="_blank" style={{ color:"#1e40af" }}>panel IONOS → Email → Mail Policy</a>
                      </div>
                    )}
                  </div>
                  <div>
                    <label style={sLabel}>Nombre del remitente</label>
                    <input value={smtpConfig.from_name||""} placeholder={`${branding.firma} — ${branding.nombre}`}
                      onChange={e => setSmtpConfig(p=>({...p,from_name:e.target.value}))} style={sInput} />
                  </div>
                  <div>
                    <label style={sLabel}>Reply-To</label>
                    <input value={smtpConfig.reply_to||""} placeholder="contacto@tudominio.com"
                      onChange={e => setSmtpConfig(p=>({...p,reply_to:e.target.value}))} style={sInput} />
                  </div>
                  {/* Host manual — visible solo en "Otro" */}
                  {!PROVEEDORES_SMTP.slice(0,-1).find(p=>p.host===smtpConfig.host) && (
                    <>
                      <div>
                        <label style={sLabel}>Servidor SMTP</label>
                        <input value={smtpConfig.host||""} placeholder="mail.tudominio.com"
                          onChange={e => setSmtpConfig(p=>({...p,host:e.target.value}))} style={sInput} />
                      </div>
                      <div>
                        <label style={sLabel}>Puerto</label>
                        <input value={smtpConfig.port||""} placeholder="587"
                          onChange={e => setSmtpConfig(p=>({...p,port:e.target.value}))} style={sInput} />
                      </div>
                    </>
                  )}
                </div>

                <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                  <button onClick={probarSmtp} style={sBtn(false)}>{smtpStatus==="testing" ? "⏳ Probando..." : "📤 Probar (lead 9999)"}</button>
                  <button onClick={guardarSmtp} style={sBtn(true)}>{smtpStatus==="saving" ? "⏳ Guardando..." : "💾 Guardar SMTP"}</button>
                  {smtpStatus==="ok" && <span style={{ fontSize:12, color:"#059669" }}>✓ OK</span>}
                  {smtpStatus==="error" && <span style={{ fontSize:12, color:"#DC2626" }}>✗ Revisá credenciales</span>}
                </div>
              </div>

              {/* ── SECCIÓN 4: BANDEJA DE ENTRADA (captar leads externos) ── */}
              <div style={sCard}>
                <div style={{ fontWeight:700, fontSize:14, color:"var(--brand-ink)", marginBottom:4 }}>4. Bandeja de entrada</div>
                <div style={{ fontSize:12, color:"rgba(var(--brand-ink-rgb),0.4)", marginBottom:14 }}>Conectá un email IMAP para captar leads de formularios web, Shopify, Typeform, etc. El agente lee la bandeja, extrae los datos y los suma como leads nuevos.</div>

                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
                  <label style={{ fontSize:13, fontWeight:600, color:"var(--brand-ink)" }}>Activar lectura</label>
                  <button onClick={() => setEmailInbox(p => ({...p, activo: !p.activo}))} style={{
                    width:44, height:24, borderRadius:12, border:"none", cursor:"pointer", position:"relative",
                    background: emailInbox.activo ? "#10B981" : "rgba(var(--brand-ink-rgb),0.15)", transition:"background 0.2s",
                  }}>
                    <span style={{
                      position:"absolute", top:2, left: emailInbox.activo ? 22 : 2,
                      width:20, height:20, borderRadius:"50%", background:"#fff", transition:"left 0.2s",
                      boxShadow:"0 1px 3px rgba(0,0,0,0.2)",
                    }} />
                  </button>
                  <span style={{ fontSize:11, color: emailInbox.activo ? "#10B981" : "rgba(var(--brand-ink-rgb),0.35)", fontWeight:600 }}>
                    {emailInbox.activo ? "Activo" : "Inactivo"}
                  </span>
                </div>

                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
                  <div>
                    <label style={sLabel}>EMAIL (IMAP)</label>
                    <input value={emailInbox.email} onChange={e => setEmailInbox(p=>({...p, email:e.target.value}))}
                      placeholder="leads@tuempresa.com" style={sInput} />
                  </div>
                  <div>
                    <label style={sLabel}>CONTRASEÑA</label>
                    <input type="password" value={emailInbox.password} onChange={e => setEmailInbox(p=>({...p, password:e.target.value}))}
                      placeholder="••••••••" style={sInput} />
                  </div>
                  <div>
                    <label style={sLabel}>SERVIDOR IMAP</label>
                    <input value={emailInbox.host} onChange={e => setEmailInbox(p=>({...p, host:e.target.value}))}
                      placeholder="imap.gmail.com" style={sInput} />
                  </div>
                  <div>
                    <label style={sLabel}>PUERTO</label>
                    <input type="number" value={emailInbox.port} onChange={e => setEmailInbox(p=>({...p, port:parseInt(e.target.value)||993}))}
                      style={sInput} />
                  </div>
                </div>

                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
                  <div>
                    <label style={sLabel}>FILTRO POR REMITENTE</label>
                    <input value={emailInbox.filtro_remitente} onChange={e => setEmailInbox(p=>({...p, filtro_remitente:e.target.value}))}
                      placeholder="noreply@shopify.com" style={sInput} />
                    <div style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.3)", marginTop:3 }}>Solo leer emails de este remitente. Vacío = todos.</div>
                  </div>
                  <div>
                    <label style={sLabel}>FILTRO POR ASUNTO</label>
                    <input value={emailInbox.filtro_asunto} onChange={e => setEmailInbox(p=>({...p, filtro_asunto:e.target.value}))}
                      placeholder="Nuevo pedido, Formulario contacto" style={sInput} />
                    <div style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.3)", marginTop:3 }}>Palabras clave en el asunto, separadas por coma.</div>
                  </div>
                </div>

                <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
                  <div>
                    <label style={sLabel}>FRECUENCIA DE LECTURA (minutos)</label>
                    <input type="number" value={emailInbox.frecuencia_min} onChange={e => setEmailInbox(p=>({...p, frecuencia_min:parseInt(e.target.value)||30}))}
                      min={5} max={1440} style={{...sInput, width:100}} />
                  </div>
                  <div style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.3)", marginTop:16 }}>Cada cuántos minutos el agente revisa la bandeja.</div>
                </div>

                {inboxStatus && (
                  <div style={{ marginBottom:12, padding:"8px 14px", borderRadius:8, fontSize:12, fontWeight:600,
                    background: inboxStatus.ok ? "#10B98120" : "#EF444420",
                    color: inboxStatus.ok ? "#10B981" : "#EF4444",
                    border: `1px solid ${inboxStatus.ok ? "#10B981" : "#EF4444"}` }}>
                    {inboxStatus.texto}
                  </div>
                )}

                <button onClick={() => {
                  setInboxStatus(null);
                  fetch(`/api/email-inbox/${EMPRESA_SLUG}`, {
                    method:"POST", headers:{"Content-Type":"application/json"},
                    body: JSON.stringify(emailInbox),
                  })
                  .then(r => r.json())
                  .then(d => {
                    if (d.ok) setInboxStatus({ ok:true, texto:"Configuración de bandeja guardada correctamente." });
                    else setInboxStatus({ ok:false, texto:"Error al guardar la configuración." });
                  })
                  .catch(() => setInboxStatus({ ok:false, texto:"Error de conexión." }));
                }} style={sBtn(true)}>💾 Guardar bandeja</button>
              </div>

              {/* ── SECCIÓN 5: USUARIOS ── */}
              <div style={sCard}>
                <div style={{ fontWeight:700, fontSize:14, color:"var(--brand-ink)", marginBottom:4 }}>5. Usuarios</div>
                <div style={{ fontSize:12, color:"rgba(var(--brand-ink-rgb),0.4)", marginBottom:4 }}>Cada usuario extra entra con su nombre y un PIN de 4 dígitos.</div>
                <div style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.35)", marginBottom:12, fontStyle:"italic" }}>Plan base: 1 admin + hasta {MAX_VENDEDORES_PLAN - 1} usuarios extra.</div>

                <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:12 }}>
                  {vendedores.map((v,i) => (
                    <div key={v.id} style={{
                      display:"flex", alignItems:"center", justifyContent:"space-between",
                      padding:"8px 10px", border:"1px solid rgba(var(--brand-ink-rgb),0.1)", borderRadius:6,
                    }}>
                      <span style={{ fontSize:13, color:"var(--brand-ink)" }}>
                        {v.nombre} {v.rol==="admin" && <span style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.4)" }}>(admin)</span>}
                      </span>
                      <span style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <span style={{ fontFamily:"monospace", fontSize:12, color:"rgba(var(--brand-ink-rgb),0.5)" }}>PIN {v.pin}</span>
                        {i > 0 && (
                          <button onClick={() => eliminarVendedor(v.id)}
                            style={{ background:"none", border:"none", color:"#DC2626", cursor:"pointer", fontSize:12 }}>
                            Eliminar
                          </button>
                        )}
                      </span>
                    </div>
                  ))}
                </div>

                {vendedores.length < MAX_VENDEDORES_PLAN ? (
                  <div style={{ display:"flex", gap:8 }}>
                    <input value={nuevoVendedorNombre} onChange={e => setNuevoVendedorNombre(e.target.value)}
                      placeholder="Nombre del nuevo usuario" style={{ ...sInput, flex:1 }} />
                    <button onClick={agregarVendedor} style={sBtn(true)}>+ Agregar</button>
                  </div>
                ) : (
                  <div style={{ fontSize:12, color:"rgba(var(--brand-ink-rgb),0.4)", fontStyle:"italic" }}>
                    Llegaste al límite de usuarios de tu plan. Para sumar más, hay que pasar a un plan superior.
                  </div>
                )}
              </div>

              {/* ── SECCIÓN 6: CONOCIMIENTO (ancho completo) ── */}
              <div style={sCard}>
                <div style={{ fontWeight:700, fontSize:14, color:"var(--brand-ink)", marginBottom:4 }}>6. Conocimiento del agente</div>
                <div style={{ fontSize:12, color:"rgba(var(--brand-ink-rgb),0.4)", marginBottom:16 }}>El "alma" del agente: quién sos, qué ofrecés y cómo calificar un buen lead.</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:10, marginBottom:14 }}>
                  {[
                    { icon:"🏢", label:"Perfil de empresa",    desc:"Quiénes son, qué ofrecen" },
                    { icon:"💰", label:"Lista de precios",      desc:"Precios por tipo de servicio" },
                    { icon:"❓", label:"Objeciones frecuentes", desc:"Cómo responder las comunes" },
                    { icon:"📋", label:"Protocolo de ventas",   desc:"Cómo trabajar cada oportunidad" },
                  ].map(item => (
                    <div key={item.label} style={{ display:"flex", flexDirection:"column", gap:8, padding:"14px", background:"rgba(var(--brand-ink-rgb),0.02)", borderRadius:10, border:"1px solid rgba(var(--brand-ink-rgb),0.07)" }}>
                      <span style={{ fontSize:22 }}>{item.icon}</span>
                      <div>
                        <div style={{ fontSize:13, fontWeight:600, color:"var(--brand-ink)", marginBottom:2 }}>{item.label}</div>
                        <div style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.4)" }}>{item.desc}</div>
                      </div>
                      <label style={{ marginTop:"auto", padding:"6px 12px", borderRadius:7, border:"1px solid rgba(var(--brand-ink-rgb),0.2)", background:"rgba(var(--brand-ink-rgb),0.04)", color:"var(--brand-ink)", fontSize:11, cursor:"pointer", fontWeight:600, textAlign:"center" }}>
                        Subir archivo
                        <input type="file" accept=".pdf,.docx,.xlsx,.txt,.csv" style={{ display:"none" }}
                          onChange={e => {
                            const file = e.target.files[0];
                            if (!file) return;
                            const reader = new FileReader();
                            reader.onload = ev => {
                              fetch(`/api/knowledge/${configEmpresa}`, {
                                method:"POST",
                                headers:{ "Content-Type":"application/octet-stream", "X-Filename":file.name },
                                body: ev.target.result,
                              }).then(r=>r.json()).then(d => {
                                if (d.ok) {
                                  alert(`✅ "${file.name}" procesado`);
                                  setKnowledgeVersion(v => v + 1);
                                } else {
                                  alert(`❌ Error: ${d.error}`);
                                }
                              }).catch(() => alert("❌ Error al subir"));
                            };
                            reader.readAsArrayBuffer(file);
                            e.target.value = "";
                          }}
                        />
                      </label>
                    </div>
                  ))}
                </div>
                <KnowledgeViewer empresa={configEmpresa} refreshKey={knowledgeVersion} />
              </div>
            </div>
          );
        })()}

        {/* ── STATUS SISTEMA ── */}
        {tab === "dashboard" && sysStatus && (
          <div style={{ marginTop:14, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:18 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <div style={{
                  width:8, height:8, borderRadius:"50%",
                  background: Object.values(sysStatus.procesos).every(p=>p.ok) ? "#10B981" : "#F59E0B",
                  boxShadow: "0 0 6px " + (Object.values(sysStatus.procesos).every(p=>p.ok) ? "#10B981" : "#F59E0B"),
                }}/>
                <span style={{ fontWeight:700, fontSize:13, color:"var(--brand-ink)" }}>
                  {Object.values(sysStatus.procesos).every(p=>p.ok) ? "Sistema operativo" : "Sistema con advertencias"}
                </span>
              </div>
              {sysStatus.ultima_actualizacion && (
                <span style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.3)" }}>
                  Ultima actualizacion: {sysStatus.ultima_actualizacion}
                </span>
              )}
            </div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {Object.entries(sysStatus.procesos)
                .filter(([nombre]) => nombre === "captio-api" || nombre.includes(EMPRESA_SLUG))
                .map(([nombre, estado]) => (
                <div key={nombre} style={{
                  display:"flex", alignItems:"center", gap:6, padding:"5px 12px", borderRadius:20,
                  background: estado.ok ? "rgba(16,185,129,0.1)" : "rgba(245,158,11,0.1)",
                  border: "1px solid " + (estado.ok ? "rgba(16,185,129,0.2)" : "rgba(245,158,11,0.2)"),
                }}>
                  <span style={{ fontSize:10 }}>{estado.ok ? "✅" : "⚠️"}</span>
                  <span style={{ fontSize:11, color: estado.ok ? "#10B981" : "#F59E0B", fontWeight:600 }}>{nombre}</span>
                  {!estado.ok && estado.error && (
                    <span style={{ fontSize:9, color:"rgba(var(--brand-ink-rgb),0.35)", maxWidth:120, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {estado.error.replace(/[^a-zA-Z0-9 ]/g,"").trim().slice(0,40)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── ADMIN ── */}
        {tab === "admin" && esAdmin && (
          <div>
            <div style={{ marginBottom:20 }}>
              <div style={{ fontWeight:800, fontSize:18, color:"var(--brand-ink)", marginBottom:4 }}>Panel de Control</div>
              <div style={{ fontSize:12, color:"rgba(var(--brand-ink-rgb),0.35)" }}>Performance estratégica · {branding.nombre}</div>
            </div>

            {/* FILA 1 — 6 KPI Cards estratégicos */}
            {(() => {
              const emailsEnviados = leads.filter(l=>l.estado_email).length;
              const ganados = leads.filter(l=>l.estado==="Ganado").length;
              const cotizados = leads.filter(l=>l.monto>0).length;
              const tasaCierre = cotizados>0?Math.round(ganados/cotizados*100):0;
              const totalMonto = leads.filter(l=>l.monto>0&&l.estado!=="Perdido").reduce((a,b)=>a+b.monto,0);
              const kpis = [
                { label:"Total Leads",      value: leads.length,                                    color:"var(--brand-ink)", icon:"📋" },
                { label:"Emails Enviados",  value: emailsEnviados,                                  color:"#888884", icon:"✉️" },
                { label:"Contactados",      value: leads.filter(l=>l.estado_email).length,           color:"#3B82F6", icon:"📞" },
                { label:"Validados",        value: leads.filter(l=>l.estado==="Validado").length,    color:"#8B5CF6", icon:"✅" },
                { label:"Tasa de cierre",   value: `${tasaCierre}%`,                                color:"#10B981", icon:"📈" },
                { label:"Pipeline total",   value: fmt$(totalMonto),                                 color:"#F59E0B", icon:"💰" },
              ];
              return (
                <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:12, marginBottom:20 }}>
                  {kpis.map((k,i) => (
                    <div key={i} style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:12, padding:"16px 14px", borderTop:`3px solid ${k.color}` }}>
                      <div style={{ fontSize:18, marginBottom:6 }}>{k.icon}</div>
                      <div style={{ fontSize:20, fontWeight:800, color:k.color, marginBottom:2 }}>{k.value}</div>
                      <div style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.4)", textTransform:"uppercase", letterSpacing:1 }}>{k.label}</div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* FILA 2 — 3 columnas: Leads por región · Pipeline tabla · Leads por segmento */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16, marginBottom:16 }}>

              {/* Leads por región */}
              {(() => {
                const regiones = Array.from(new Set(leads.map(l=>l.region).filter(Boolean)));
                const cols = ["var(--brand-ink)","#888884","#3B82F6","#10B981","#8B5CF6","#F59E0B","#06B6D4","#EF4444"];
                const data = regiones.map((r,i) => ({
                  label: r, color: cols[i%8],
                  total: leads.filter(l=>l.region===r).length,
                  ganados: leads.filter(l=>l.region===r&&l.estado==="Ganado").length,
                })).sort((a,b)=>b.total-a.total);
                return (
                  <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:20 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:"var(--brand-ink)", marginBottom:16 }}>📍 Leads por región</div>
                    {data.length===0 && <div style={{ fontSize:12, color:"rgba(var(--brand-ink-rgb),0.3)", textAlign:"center", padding:"20px 0" }}>Sin datos</div>}
                    {data.map(d => (
                      <div key={d.label} style={{ marginBottom:12 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                          <span style={{ fontSize:12, color:"rgba(var(--brand-ink-rgb),0.7)" }}>{d.label}</span>
                          <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                            {d.ganados>0&&<span style={{ fontSize:10, color:"#10B981" }}>✓{d.ganados}</span>}
                            <span style={{ fontSize:12, fontWeight:700, color:d.color }}>{d.total}</span>
                          </div>
                        </div>
                        <div style={{ height:5, background:"rgba(255,255,255,0.06)", borderRadius:3 }}>
                          <div style={{ height:"100%", width:`${Math.round(d.total/Math.max(...data.map(x=>x.total),1)*100)}%`, background:d.color, borderRadius:3 }}/>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Estado del pipeline — tabla */}
              {(() => {
                const estados = [
                  { id:"Nuevo",    color:"#6B7280" },
                  { id:"Validado", color:"#3B82F6" },
                  { id:"Cotizado", color:"#F59E0B" },
                  { id:"Ganado",   color:"#10B981" },
                  { id:"Perdido",  color:"#EF4444" },
                ];
                const data = estados.map(e => ({
                  ...e,
                  total: leads.filter(l=>l.estado===e.id).length,
                  monto: leads.filter(l=>l.estado===e.id&&l.monto>0).reduce((a,b)=>a+b.monto,0),
                }));
                const total = data.reduce((a,b)=>a+b.total,0)||1;
                return (
                  <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:20 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:"var(--brand-ink)", marginBottom:16 }}>📊 Estado del pipeline</div>
                    <table width="100%" cellPadding="0" cellSpacing="0" style={{ borderCollapse:"collapse" }}>
                      <thead>
                        <tr>
                          <td style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.35)", paddingBottom:8, textTransform:"uppercase", letterSpacing:1 }}>Estado</td>
                          <td style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.35)", paddingBottom:8, textAlign:"right", textTransform:"uppercase", letterSpacing:1 }}>N°</td>
                          <td style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.35)", paddingBottom:8, textAlign:"right", textTransform:"uppercase", letterSpacing:1 }}>Monto</td>
                        </tr>
                      </thead>
                      <tbody>
                        {data.map(d => (
                          <tr key={d.id} style={{ borderTop:"1px solid rgba(var(--brand-ink-rgb),0.06)" }}>
                            <td style={{ padding:"9px 0", fontSize:12, color:"var(--brand-ink)" }}>
                              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                <div style={{ width:8, height:8, borderRadius:"50%", background:d.color, flexShrink:0 }}/>
                                {d.id}
                              </div>
                            </td>
                            <td style={{ padding:"9px 0", textAlign:"right" }}>
                              <span style={{ fontSize:13, fontWeight:700, color:d.color }}>{d.total}</span>
                              <span style={{ fontSize:10, color:"rgba(var(--brand-ink-rgb),0.3)", marginLeft:4 }}>{Math.round(d.total/total*100)}%</span>
                            </td>
                            <td style={{ padding:"9px 0", fontSize:11, color:"#F59E0B", textAlign:"right", fontWeight:600 }}>
                              {d.monto>0?fmt$(d.monto):"—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}

              {/* Leads por segmento */}
              {(() => {
                const segs = Array.from(new Set(leads.map(l=>l.segmento).filter(Boolean)));
                const segCols = ["var(--brand-ink)","#888884","#3B82F6","#10B981","#8B5CF6","#F59E0B","#06B6D4","#EF4444"];
                const data = segs.map((s,i) => ({
                  label: s, color: segCols[i%8],
                  total: leads.filter(l=>l.segmento===s).length,
                  ganados: leads.filter(l=>l.segmento===s&&l.estado==="Ganado").length,
                  monto: leads.filter(l=>l.segmento===s&&l.monto>0).reduce((a,b)=>a+b.monto,0),
                })).sort((a,b)=>b.total-a.total);
                const maxVal = Math.max(...data.map(d=>d.total),1);
                return (
                  <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:20 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:"var(--brand-ink)", marginBottom:16 }}>🏢 Leads por segmento</div>
                    {data.map(d => (
                      <div key={d.label} style={{ marginBottom:12 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                          <span style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.7)", maxWidth:120, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{d.label}</span>
                          <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                            {d.ganados>0&&<span style={{ fontSize:9, color:"#10B981" }}>✓{d.ganados}</span>}
                            {d.monto>0&&<span style={{ fontSize:9, color:"#F59E0B" }}>{fmt$(d.monto)}</span>}
                            <span style={{ fontSize:11, fontWeight:700, color:d.color }}>{d.total}</span>
                          </div>
                        </div>
                        <div style={{ height:5, background:"rgba(255,255,255,0.06)", borderRadius:3 }}>
                          <div style={{ height:"100%", width:`${Math.round(d.total/maxVal*100)}%`, background:d.color, borderRadius:3 }}/>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

            </div>

            {/* FILA 3 — Gráfico captación últimos 30 días */}
            {(() => {
              const hoy = new Date();
              const dias = Array.from({length:30},(_,i)=>{
                const d = new Date(hoy);
                d.setDate(hoy.getDate()-29+i);
                return d.toISOString().split("T")[0];
              });
              const counts = dias.map(dia => ({
                dia, label: dia.slice(5),
                total: leads.filter(l=>l.fecha&&l.fecha.startsWith(dia)).length,
              }));
              const maxVal = Math.max(...counts.map(c=>c.total),1);
              const W=600, H=100, PAD=20;
              const bw=(W-PAD*2)/30;
              return (
                <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:20 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
                    <div>
                      <div style={{ fontWeight:700, fontSize:13, color:"var(--brand-ink)" }}>📈 Captación de leads — últimos 30 días</div>
                      <div style={{ fontSize:11, color:"rgba(var(--brand-ink-rgb),0.35)", marginTop:2 }}>Total: {counts.reduce((a,c)=>a+c.total,0)} leads captados</div>
                    </div>
                  </div>
                  <svg width="100%" viewBox={`0 0 ${W} ${H+36}`} style={{ display:"block" }}>
                    {[0,0.25,0.5,0.75,1].map((p,i)=>(
                      <line key={i} x1={PAD} y1={PAD+H*(1-p)} x2={W-PAD} y2={PAD+H*(1-p)} stroke="rgba(var(--brand-ink-rgb),0.06)" strokeWidth="1"/>
                    ))}
                    {counts.map((c,i)=>{
                      const bh=maxVal>0?Math.max((c.total/maxVal)*H,c.total>0?3:0):0;
                      const x=PAD+i*bw+bw*0.15;
                      const bwActual=bw*0.7;
                      return (
                        <g key={i}>
                          <rect x={x} y={PAD+H-bh} width={bwActual} height={bh} fill={c.total>0?"var(--brand-ink)":"rgba(var(--brand-ink-rgb),0.06)"} rx="2"/>
                          {c.total>0&&<text x={x+bwActual/2} y={PAD+H-bh-4} textAnchor="middle" fill="#888884" fontSize="9" fontWeight="700">{c.total}</text>}
                        </g>
                      );
                    })}
                    {counts.filter((_,i)=>i%5===0||i===29).map((c)=>(
                      <text key={c.dia} x={PAD+(counts.indexOf(c))*bw+bw/2} y={PAD+H+16} textAnchor="middle" fill="rgba(var(--brand-ink-rgb),0.35)" fontSize="9">{c.label}</text>
                    ))}
                  </svg>
                </div>
              );
            })()}

          </div>
        )}
      </div>

      {/* MODALES */}
      {modal?.tipo === "mover" && (
        <ModalMover
          lead={modal.lead}
          destino={modal.destino}
          onConfirm={confirmarMover}
          onCancel={() => setModal(null)}
        />
      )}
      {modal?.tipo === "detalle" && (
        <ModalDetalle
          lead={modal.lead}
          vendedores={vendedores}
          onClose={() => setModal(null)}
          onAsignar={asignarVendedor}
          onMoverACotizado={moverACotizado}
          esAdmin={esAdmin}
          usuarioActual={usuario}
        />
      )}
    </div>
  );
}
